// @ts-nocheck
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ReactionDisplay } from './ReactionDisplay';
import { copyToClipboard, readFromClipboard } from '../utils/helpers';
import { UserData, Notification, GlobalSettingsModalProps, Post } from '../types';
import { useScrollLock } from '../hooks/useScrollLock';
import { useAuth, useAudioPlayerState } from '../contexts/AuthContext';
import { compressImage, uploadMediaFile } from '../utils/mediaUtils';
import api, { API_URL, updateUserOnServer, deactivateAccount, deleteAccountPermanently, updateEmail, sendSystemBroadcast, deleteSystemBroadcast, editSystemBroadcast, getAutoBroadcastIds, toggleAutoBroadcast } from '../api';
import { setupPushNotifications } from '../utils/notificationUtils';
import { handleSliderPointerDown } from '../utils/helpers';
import {
    LogoutIcon, CloseIcon, CopyIcon, PasteIcon, SparklesIcon, ImageIcon,
    SearchIcon, BellIcon, SettingsIcon, CheckmarkIcon, PlusIcon,
    VolumeHighIcon, SendIcon, ChevronRightIcon, ChevronLeftIcon, TrashIcon, EditIcon,
    AtIcon, LikeIcon, UsersIcon, CommentIcon, BumpIcon, UserIcon, EyeIcon, EyeOffIcon
} from './Icons';
import { EmojiPickerModal } from './EmojiPickerModal';
import { GifPickerModal } from './GifPickerModal';
import { STORE_ITEMS, CATEGORIES, FONT_OPTIONS } from '../constants';
import { PimpMyRide } from './PimpMyRide';
import { CustomFontSelect } from './Modals';
import { FormattedText } from './FormattedText';
import { stickerTextStyle as baseStickerTextStyle } from './StickerText';
import { Capacitor } from '@capacitor/core';
import { updateAppShortcuts } from '../utils/nativeShortcuts';
import { SlideshowBuilder } from './SlideshowBuilder';
import { getAllMods, ModIcon } from '../utils/ModRegistry';
import { getAppLogo } from '../utils/flavor';
import { AppIcon } from '@capacitor-community/app-icon';

const formatRelativeTime = (dateString?: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = Math.max(0, now.getTime() - date.getTime());
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 10) return "Just now!";
    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;

    return date.toLocaleString([], {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const isVideoUrl = (url: string | undefined): boolean => {
    if (!url) return false;
    return url.includes('#video') || /\.(mp4|webm|ogg|mov|m4v)($|\?)/i.test(url);
};

type SettingsTab = 'search' | 'notifs' | 'store' | 'settings';

const NotificationMediaCarousel = ({ media }: { media: any[] }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    const handlePrev = (e: React.MouseEvent) => {
        e.stopPropagation();
        setCurrentIndex(prev => prev === 0 ? media.length - 1 : prev - 1);
    };

    const handleNext = (e: React.MouseEvent) => {
        e.stopPropagation();
        setCurrentIndex(prev => prev === media.length - 1 ? 0 : prev + 1);
    };

    if (!media || media.length === 0) return null;

    const currentMedia = media[currentIndex];

    return (
        <div className="mt-3 relative rounded-2xl overflow-hidden border border-white/5 bg-black/20 group">
            {currentMedia.type === 'video' ? (
                <video src={`${currentMedia.url}#t=0.001`} className="w-full max-h-[80vh] object-contain bg-black" controls playsInline />
            ) : currentMedia.type === 'audio' ? (
                <audio src={currentMedia.url} className="w-full" controls />
            ) : (
                <img src={currentMedia.url} className="w-full max-h-[80vh] object-contain bg-black" />
            )}

            {media.length > 1 && (
                <>
                    <div className="absolute inset-y-0 left-0 flex items-center pl-2 z-10">
                        <button onClick={handlePrev} className="p-2 rounded-full bg-black/50 text-white hover:bg-black/80 backdrop-blur-md">
                            <ChevronLeftIcon className="w-5 h-5" />
                        </button>
                    </div>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 z-10">
                        <button onClick={handleNext} className="p-2 rounded-full bg-black/50 text-white hover:bg-black/80 backdrop-blur-md">
                            <ChevronRightIcon className="w-5 h-5" />
                        </button>
                    </div>
                    <div className="absolute bottom-3 inset-x-0 flex justify-center gap-1.5 z-20">
                        {media.map((_, i) => (
                            <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === currentIndex ? 'w-4 bg-white shadow-[0_0_8px_rgba(255,255,255,0.8)]' : 'w-1.5 bg-white/50 hover:bg-white/70'}`} />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export const GlobalSettingsModal = ({
    isOpen,
    onClose,
    bg,
    onBgChange,
    profileBg,
    onProfileBgChange,
    homeBgResolution,
    onHomeBgResolutionChange,
    profileBgResolution,
    onProfileBgResolutionChange,
    currentAnimation,
    onAnimationChange,
    currentOpacity,
    onOpacityChange,
    onLogout,
    autoApproveFollows,
    onAutoApproveFollowsChange,
    allowFollowers,
    onAllowFollowersChange,
    showCommunityPostsOnHomeFeed,
    onShowCommunityPostsOnHomeFeedChange,
    enableNavbarAutohide,
    onEnableNavbarAutohideChange,
    stickyFeaturedLyfe,
    onStickyFeaturedLyfeChange,
    enforceThemeColor,
    onEnforceThemeColorChange,
    enforceThemeOpacity,
    onEnforceThemeOpacityChange,
    notificationSettings,
    onNotificationSettingsChange,
    enforceTextColor,
    enforceTextOutlineColor,
    onEnforceTextColorChange,
    onEnforceTextOutlineColorChange,
    onNavigateToPrivacy,
    onNavigateToSafety,
    onNavigateToAdditionalSettings,
    onNavigateToStore,
    onShare,
    commentsTabVisibility,
    onCommentsTabVisibilityChange,
    favoriteEmoji,
    onFavoriteEmojiChange,
    postcardDefault,
    onPostcardDefaultChange,
    postcardAppearance,
    onPostcardAppearanceChange,
    notifications = [],
    onMarkAsRead,
    onNavigateToPost,
    onNavigateToProfile,
    onNavigateToCommunityChannel,
    onNavigateToFootprint,
    allUsers = {},
    hasUnreadNotifications,
    initialTab,
    onTabChange,
    allPosts: propPosts = [],
    onOpenSystemModModal,
    onNavigateToHashtag,
    homeBgMode = 'image',
    onHomeBgModeChange,
    homeBgColor = '#000000',
    onHomeBgColorChange,
    profileBgMode = 'image',
    onProfileBgModeChange,
    profileBgColor = '#000000',
    onProfileBgColorChange,
    homeBgImage,
    profileBgImage,
    uiScale = 1.0,
    onUiScaleChange,
    loginBg,
    onLoginBgChange,
    loginBgMode = 'image',
    onLoginBgModeChange,
    loginBgColor = '#000000',
    onLoginBgColorChange,
    loginBgResolution = 100,
    onLoginBgResolutionChange,
    loginBgSlideshow,
    onLoginBgSlideshowChange,
    onOpenDebug,
    isAdmin,
    onNavigateToMasterHand,
    onOpenIdeaBank,
    communities = [],
    onNavigateToPage,
    onNavigateToChat,
    onNavigateToMod,
    onNavigateToCommunities,
    homeBgSlideshow,
    onHomeBgSlideshowChange,
    profileBgSlideshow,
    onProfileBgSlideshowChange
}: GlobalSettingsModalProps) => {
    const { currentUser, usersData, updateUserData, isDemo, loggedInAccounts } = useAuth();
    const { autoOpenNowPlaying, setAutoOpenNowPlaying } = useAudioPlayerState();
    const userData = usersData[currentUser || ''];
    const userTextColor = userData?.appTextColor || '#ffffff';
    const stickerTextStyle = (whiteOpacity: number = 0.35, textColor?: string) => {
        return baseStickerTextStyle(whiteOpacity, textColor || userTextColor);
    };

    const [activeTab, setActiveTab] = useState<SettingsTab>(initialTab || 'settings');
    const [tabSwipeDirection, setTabSwipeDirection] = useState<'left' | 'right' | null>(null);
    const [isTabSwiping, setIsTabSwiping] = useState(false);

    const defaultSettingsStmt = `This app was brought to you by VibeMan (@vibeman1) & GoodieBoy (@goodlyfe). They made this cuz literally nobody likes social media. Our options are not fun, they're brainrotting, addicting thieves of time and joy. Ppl yearn for feeds with an end. No algorithms, no ads, no tracking, no analytics, no promoted content, no surveilence. just ppl and topics you actually follow. It's what you make it. Ppl don't even post cuz everything feels like a drop. Stop being so serious. Have some fun and post all you want. There's no stories. Just posts. Good ol fashioned social media. More features to come, and they'll actually be fun! ${((window as any).Capacitor?.getPlatform() !== 'ios') ? "If you like what you see, consider donating to keep things running. " : ""} We're making the Wikipedia of social media!`;
    const currentSettingsStmt = usersData['goodlyfe']?.settingsStatement || defaultSettingsStmt;
    const [settingsStmtInput, setSettingsStmtInput] = useState(currentSettingsStmt);

    useEffect(() => {
        setSettingsStmtInput(currentSettingsStmt);
    }, [currentSettingsStmt]);

    const tabsList: SettingsTab[] = ['settings', 'search', 'notifs', 'store'];

    const contentRef = useRef<HTMLDivElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);
    const [isExiting, setIsExiting] = useState(false);
    const [exitDirection, setExitDirection] = useState<'left' | 'right'>('right');

    const triggerExit = (direction: 'left' | 'right', targetPage?: any) => {
        setExitDirection(direction);
        setIsExiting(true);
        setTimeout(() => {
            if (targetPage) {
                const event = new CustomEvent('app-navigate', { detail: targetPage });
                window.dispatchEvent(event);
            }
            onClose();
        }, 300);
    };

    const handleTabSwitch = (newTab: SettingsTab) => {
        if (newTab === activeTab) return;
        const oldIdx = tabsList.indexOf(activeTab);
        const newIdx = tabsList.indexOf(newTab);
        setTabSwipeDirection(newIdx > oldIdx ? 'left' : 'right');
        setIsTabSwiping(true);
        setTimeout(() => setIsTabSwiping(false), 600);
        setActiveTab(newTab);
        onTabChange?.(newTab);
    };

    useEffect(() => {
        if (!isOpen) {
            setIsExiting(false);
            setExitDirection('right');
        }
    }, [isOpen]);

    // Sync activeTab with initialTab prop
    useEffect(() => {
        if (initialTab) {
            setActiveTab(initialTab);
        }
    }, [initialTab]);

    // Initial tab selection based on notifications if no initialTab
    useEffect(() => {
        if (isOpen && hasUnreadNotifications && !initialTab) {
            setActiveTab('notifs');
        }
    }, [isOpen, hasUnreadNotifications, initialTab]);

    // --- SHARED STATE & REFS ---
    const bgFileInputRef = useRef<HTMLInputElement>(null);
    const profileBgFileInputRef = useRef<HTMLInputElement>(null);
    const homeNativePhotosInputRef = useRef<HTMLInputElement>(null);
    const profileNativePhotosInputRef = useRef<HTMLInputElement>(null);
    const loginNativePhotosInputRef = useRef<HTMLInputElement>(null);
    const styleOptions = ['tile', 'color'];
    // userData is already declared at the top of the component

    // Slideshow state hooks
    const [tenorTarget, setTenorTarget] = useState<'home' | 'profile' | 'login' | null>(null);

    const [photosTarget, setPhotosTarget] = useState<'home' | 'profile' | 'login' | null>(null);
    const [photosProvider, setPhotosProvider] = useState<'apple' | 'google' | null>(null);
    const [photosAuthStep, setPhotosAuthStep] = useState<'none' | 'login' | 'browsing'>('none');
    const [photosUsername, setPhotosUsername] = useState('');
    const [photosPassword, setPhotosPassword] = useState('');
    const [photosConnecting, setPhotosConnecting] = useState(false);
    const [selectedPhotosAlbum, setSelectedPhotosAlbum] = useState<string | null>(null);
    const [albumSelection, setAlbumSelection] = useState<string[]>([]);

    const googleAlbums = [
        {
            id: 'g_summer',
            name: 'Summer Vibe 2025',
            count: 3,
            photos: [
                'https://images.unsplash.com/photo-1507525428034-b723a996f6ea?q=80&w=800&auto=format&fit=crop',
                'https://images.unsplash.com/photo-1473116763269-255ea7604668?q=80&w=800&auto=format&fit=crop',
                'https://images.unsplash.com/photo-1506929562872-bb421503ef21?q=80&w=800&auto=format&fit=crop'
            ]
        },
        {
            id: 'g_city',
            name: 'Neon Cityscapes',
            count: 3,
            photos: [
                'https://images.unsplash.com/photo-1519608487953-e999c86e7455?q=80&w=800&auto=format&fit=crop',
                'https://images.unsplash.com/photo-1514565131-fce0801e5785?q=80&w=800&auto=format&fit=crop',
                'https://images.unsplash.com/photo-1542838132-92c53300491e?q=80&w=800&auto=format&fit=crop'
            ]
        }
    ];

    const appleAlbums = [
        {
            id: 'a_favorites',
            name: 'Favorites',
            count: 3,
            photos: [
                'https://images.unsplash.com/photo-1490730141103-6cac27aaab94?q=80&w=800&auto=format&fit=crop',
                'https://images.unsplash.com/photo-1447752875215-b2761acb3c5d?q=80&w=800&auto=format&fit=crop',
                'https://images.unsplash.com/photo-1472214222541-d510753a4707?q=80&w=800&auto=format&fit=crop'
            ]
        },
        {
            id: 'a_vapor',
            name: 'Vaporwave Style',
            count: 2,
            photos: [
                'https://images.unsplash.com/photo-1550684848-fac1c5b4e853?q=80&w=800&auto=format&fit=crop',
                'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=800&auto=format&fit=crop'
            ]
        }
    ];

    const handleNativeSlideshowUpload = async (event: React.ChangeEvent<HTMLInputElement>, target: 'home' | 'profile' | 'login') => {
        const files = event.target.files;
        if (!files || files.length === 0 || !currentUser) return;

        if (target === 'home') {
            onHomeBgModeChange?.('image');
        } else if (target === 'profile') {
            onProfileBgModeChange?.('image');
        } else {
            onLoginBgModeChange?.('image');
        }

        const currentSlideshow = target === 'home' 
            ? homeBgSlideshow || { enabled: true, attachments: [], intervalValue: 10, intervalUnit: 'seconds' }
            : target === 'profile'
                ? profileBgSlideshow || { enabled: true, attachments: [], intervalValue: 10, intervalUnit: 'seconds' }
                : loginBgSlideshow || { enabled: true, attachments: [], intervalValue: 10, intervalUnit: 'seconds' };

        const newAttachments = [...(currentSlideshow.attachments || [])];

        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const isVideo = file.type.startsWith('video/');
            const tempUrl = URL.createObjectURL(file) + (isVideo ? "#video" : "");
            const tempId = `temp-${Date.now()}-${i}`;
            
            newAttachments.push({
                id: tempId,
                url: tempUrl,
                type: isVideo ? 'video' : 'image',
                name: file.name,
                resolution: 100,
                durationValue: 10,
                durationUnit: 'seconds',
                style: isVideo ? 'cover' : 'tile'
            });
        }

        const tempSlideshow = { ...currentSlideshow, attachments: newAttachments };
        if (target === 'home') {
            onHomeBgSlideshowChange?.(tempSlideshow);
        } else if (target === 'profile') {
            onProfileBgSlideshowChange?.(tempSlideshow);
        } else {
            onLoginBgSlideshowChange?.(tempSlideshow);
        }

        try {
            const uploadedAttachments = [...(currentSlideshow.attachments || [])];
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const isVideo = file.type.startsWith('video/');
                const isPreservedType = isVideo || ['image/gif', 'image/webp', 'image/apng'].includes(file.type) || /\.(gif|webp|mp4|webm|ogg|mov|m4v)$/i.test(file.name);
                let fileToUpload = file;
                if (!isPreservedType) {
                    try {
                        const compressedBlob = await compressImage(file);
                        fileToUpload = new File([compressedBlob], file.name.replace(/\.[^/.]+$/, "") + ".png", { type: 'image/png' });
                    } catch (e) {
                        console.error("Compression failed", e);
                    }
                }
                const url = await uploadMediaFile(fileToUpload, currentUser);
                if (url) {
                    uploadedAttachments.push({
                        id: `attachment-${Date.now()}-${i}`,
                        url: url,
                        type: isVideo ? 'video' : 'image',
                        name: file.name,
                        resolution: 100,
                        durationValue: 10,
                        durationUnit: 'seconds',
                        style: isVideo ? 'cover' : 'tile'
                    });
                }
            }
            const finalSlideshow = { ...currentSlideshow, attachments: uploadedAttachments };
            if (target === 'home') {
                onHomeBgSlideshowChange?.(finalSlideshow);
            } else if (target === 'profile') {
                onProfileBgSlideshowChange?.(finalSlideshow);
            } else {
                onLoginBgSlideshowChange?.(finalSlideshow);
            }
        } catch (err) {
            console.error("Multiple upload failed", err);
        }
    };

    const activeHomeSlideshow = homeBgSlideshow || { enabled: true, attachments: [], intervalValue: 10, intervalUnit: 'seconds' };
    const activeProfileSlideshow = profileBgSlideshow || { enabled: true, attachments: [], intervalValue: 10, intervalUnit: 'seconds' };
    const activeLoginSlideshow = loginBgSlideshow || { enabled: true, attachments: [], intervalValue: 10, intervalUnit: 'seconds' };

    const [followerNickname, setFollowerNickname] = useState(userData?.followerNickname || 'Followers');
    const [bio, setBio] = useState(userData?.bio || '');
    const [passion, setPassion] = useState(userData?.passion || '');
    const [passionError, setPassionError] = useState<string | null>(null);
    const [isCheckingPassion, setIsCheckingPassion] = useState(false);
    const [displayName, setDisplayName] = useState(userData?.displayName || currentUser || '');
    const [isVendor, setIsVendor] = useState(userData?.isVendor || false);
    const [vendorName, setVendorName] = useState(userData?.vendorName || '');
    const [vendorError, setVendorError] = useState<string | null>(null);
    const [localHomeBgResolution, setLocalHomeBgResolution] = useState(() => {
        const val = homeBgResolution ?? 100;
        return val > 200 ? 200 : (val < 25 ? 25 : val);
    });
    const [localProfileBgResolution, setLocalProfileBgResolution] = useState(() => {
        const val = profileBgResolution ?? 100;
        return val > 200 ? 200 : (val < 25 ? 25 : val);
    });
    const [localLoginBgResolution, setLocalLoginBgResolution] = useState(() => {
        const val = loginBgResolution ?? 100;
        return val > 200 ? 200 : (val < 25 ? 25 : val);
    });
    const [localAppThemeColor, setLocalAppThemeColor] = useState<string>(userData?.appThemeColor || '#161b22');
    const [localAppIconOverride, setLocalAppIconOverride] = useState<string>(userData?.appIconOverride || 'goodlyfe');
    const [localAppTextColor, setLocalAppTextColor] = useState<string>(userData?.appTextColor || '#ffffff');
    const [localAppTextOutlineColor, setLocalAppTextOutlineColor] = useState<string>(userData?.appTextOutlineColor || '');
    const [localAppFontFamily, setLocalAppFontFamily] = useState<string>(userData?.appFontFamily || 'Inter');
    const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
    const [isSocialsOpen, setIsSocialsOpen] = useState(false);
    const [socialsState, setSocialsState] = useState<NonNullable<UserData['socials']>>({});

    useScrollLock(isEmojiPickerOpen);
    const [localFavoriteEmoji, setLocalFavoriteEmoji] = useState(favoriteEmoji || userData?.favoriteEmoji || '❤️');

    // OAuth popup callback listener
    useEffect(() => {
        const handleOAuthMessage = (event: MessageEvent) => {
            if (!event.data || typeof event.data !== 'object') return;
            if (event.data.type === 'GOODLYFE_OAUTH_SUCCESS') {
                const { provider, username, displayName, accessToken } = event.data.payload || {};
                if (!provider || !username) return;

                const updatedConn = {
                    ...(userData?.connectedAccounts || {}),
                    [provider]: {
                        username,
                        displayName: displayName || `@${username}`,
                        accessToken: accessToken || 'verified_token',
                        connectedAt: new Date().toISOString()
                    }
                };

                if (updateUserData && currentUser) {
                    updateUserData((prev: Record<string, UserData>) => ({
                        ...prev,
                        [currentUser]: {
                            ...prev[currentUser],
                            connectedAccounts: updatedConn,
                            socials: { ...prev[currentUser].socials, [provider]: username }
                        }
                    }));
                }
            }
        };

        window.addEventListener('message', handleOAuthMessage);
        return () => window.removeEventListener('message', handleOAuthMessage);
    }, [currentUser, userData?.connectedAccounts, updateUserData]);

    useEffect(() => {
        if (!passion) {
            setPassionError(null);
            setIsCheckingPassion(false);
            return;
        }

        let formattedUrl = passion;
        if (!passion.startsWith('http')) {
            formattedUrl = `https://${passion}`;
        }

        let isValidFormat = true;
        try {
            new URL(formattedUrl);
        } catch {
            isValidFormat = false;
        }

        if (!isValidFormat) {
            setPassionError('Invalid URL format');
            setIsCheckingPassion(false);
            return;
        }

        setIsCheckingPassion(true);
        setPassionError(null);

        const timer = setTimeout(async () => {
            try {
                const response = await api.post('metadata', { url: formattedUrl });
                if (!response.data || response.data.error) {
                    setPassionError("Website couldn't be reached");
                } else {
                    setPassionError(null);
                }
            } catch (error) {
                setPassionError("Website couldn't be reached");
            } finally {
                setIsCheckingPassion(false);
            }
        }, 800);

        return () => clearTimeout(timer);
    }, [passion]);

    // Update local state when userData changes
    useEffect(() => {
        if (!userData) return;
        setFollowerNickname(userData.followerNickname || 'Followers');
        setBio(userData.bio || '');
        setPassion(userData.passion || '');
        setDisplayName(userData.displayName || currentUser || '');
        setLocalAppThemeColor(userData.appThemeColor || '#161b22');
        setLocalAppIconOverride(userData.appIconOverride || 'goodlyfe');
        setLocalAppTextColor(userData.appTextColor || '#ffffff');
        setLocalAppTextOutlineColor(userData.appTextOutlineColor || '');
        setLocalAppFontFamily(userData.appFontFamily || 'Inter');
        setLocalFavoriteEmoji(userData.favoriteEmoji || '❤️');
        setSocialsState(userData.socials || {});
    }, [userData, currentUser]);

    // Update local resolution when prop changes
    useEffect(() => {
        if (homeBgResolution !== undefined) {
            const val = homeBgResolution > 200 ? 200 : (homeBgResolution < 25 ? 25 : homeBgResolution);
            setLocalHomeBgResolution(val);
        }
        if (profileBgResolution !== undefined) {
            const val = profileBgResolution > 200 ? 200 : (profileBgResolution < 25 ? 25 : profileBgResolution);
            setLocalProfileBgResolution(val);
        }
        if (loginBgResolution !== undefined) {
            const val = loginBgResolution > 200 ? 200 : (loginBgResolution < 25 ? 25 : loginBgResolution);
            setLocalLoginBgResolution(val);
        }
    }, [homeBgResolution, profileBgResolution, loginBgResolution]);

    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<string[]>([]);
    const [hashtagResults, setHashtagResults] = useState<string[]>([]);
    const [postResults, setPostResults] = useState<Post[]>([]);
    const debounceTimeout = useRef<number | null>(null);

    // --- BROADCAST STATE ---
    const [isBroadcastModalOpen, setIsBroadcastModalOpen] = useState(false);
    const [broadcastMessage, setBroadcastMessage] = useState('');
    const [broadcastMedia, setBroadcastMedia] = useState<any[]>([]);
    const [isSendingBroadcast, setIsSendingBroadcast] = useState(false);
    const [isAutoSend, setIsAutoSend] = useState(false);
    const [broadcastTargetType, setBroadcastTargetType] = useState<'' | 'profile' | 'url' | 'community_detail' | 'mod'>('');
    const [broadcastTargetUserId, setBroadcastTargetUserId] = useState('');
    const [broadcastUserQuery, setBroadcastUserQuery] = useState('');
    const [broadcastUrlInput, setBroadcastUrlInput] = useState('');
    const [broadcastTargetCommunityId, setBroadcastTargetCommunityId] = useState('');
    const [broadcastTargetChannelId, setBroadcastTargetChannelId] = useState('');
    const [broadcastCommunityQuery, setBroadcastCommunityQuery] = useState('');
    const [broadcastTargetModId, setBroadcastTargetModId] = useState('');
    const [broadcastModQuery, setBroadcastModQuery] = useState('');
    const [selectedNotifFilters, setSelectedNotifFilters] = useState<string[]>([]);
    const [autoSendIds, setAutoSendIds] = useState<number[]>([]);
    const broadcastFileInputRef = useRef<HTMLInputElement>(null);

    // Fetch Auto-Broadcast IDs for Admins
    useEffect(() => {
        if (isOpen && activeTab === 'notifs' && userData?.role === 'admin') {
            getAutoBroadcastIds()
                .then(data => setAutoSendIds(data.ids || []))
                .catch(err => console.error('Failed to fetch auto-broadcast IDs:', err));
        }
    }, [isOpen, activeTab, userData?.role]);

    useEffect(() => {
        if (activeTab !== 'search') return;
        if (debounceTimeout.current) window.clearTimeout(debounceTimeout.current);
        debounceTimeout.current = window.setTimeout(() => {
            if (searchQuery.trim()) {
                const q = searchQuery.toLowerCase();
                const q_clean = q.replace(/^[#@]/, '');
                const normalizedQ = q_clean.replace(/\./g, '_');

                // Search Users
                const userResults = Object.keys(allUsers).filter(username => {
                    const normalizedUsername = username.toLowerCase().replace(/\./g, '_');
                    const dName = (allUsers[username]?.displayName || '').toLowerCase();
                    if ((normalizedUsername.includes(normalizedQ) || dName.includes(q_clean)) && username !== currentUser && username !== 'system') {
                        const isBlocked = (currentUser && allUsers[currentUser]?.blockedUsers || []).includes(username) ||
                            (allUsers[username]?.blockedUsers || []).includes(currentUser || '');
                        return !isBlocked;
                    }
                    return false;
                });
                setSearchResults(userResults);

                // Search Posts & Hashtags
                if (Array.isArray(propPosts)) {
                    const tagsMap = new Map<string, string>();
                    const matchedPosts: Post[] = [];

                    propPosts.forEach(p => {
                        const text = (p.content.text || '').toLowerCase();

                        // Look for content matches (always use full q for literal matches)
                        if (text.includes(q) || (q_clean && text.includes(q_clean))) {
                            matchedPosts.push(p);
                        }

                        // Look for hashtag matches (use q_clean)
                        const re = /#([a-zA-Z0-9_\-]+)/gi;
                        let m;
                        while ((m = re.exec(text)) !== null) {
                            if (m[1].toLowerCase().includes(q_clean)) tagsMap.set(m[1].toLowerCase(), m[1]);
                        }
                    });
                    setHashtagResults(Array.from(tagsMap.values()));
                    setPostResults(matchedPosts.slice(0, 20)); // Limit results
                }
            } else {
                setSearchResults([]);
                setHashtagResults([]);
                setPostResults([]);
            }
        }, 200);
        return () => { if (debounceTimeout.current) window.clearTimeout(debounceTimeout.current); };
    }, [searchQuery, allUsers, currentUser, activeTab, propPosts]);

    // --- NOTIF LOGIC ---
    useEffect(() => {
        if (activeTab === 'notifs' && isOpen && notifications.some(n => !n.read)) {
            notifications.filter(n => !n.read).forEach((n) => onMarkAsRead?.(n.id));
        }
    }, [activeTab, isOpen, notifications, onMarkAsRead]);

    // --- HANDLERS ---
    const handleDisplayNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setDisplayName(value);
        if (updateUserData && currentUser) {
            updateUserData((prev: Record<string, UserData>) => ({ ...prev, [currentUser]: { ...prev[currentUser], displayName: value } }));
            if (!isDemo) updateUserOnServer(currentUser, { displayName: value }).catch(console.error);
        }
    };

    const handleFollowerNicknameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setFollowerNickname(value);
        if (updateUserData && currentUser) {
            updateUserData((prev: Record<string, UserData>) => ({ ...prev, [currentUser]: { ...prev[currentUser], followerNickname: value } }));
            if (!isDemo) updateUserOnServer(currentUser, { followerNickname: value }).catch(console.error);
        }
    };

    const handleBioChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        if (value.length > 120) return;
        setBio(value);
        if (updateUserData && currentUser) {
            updateUserData((prev: Record<string, UserData>) => ({ ...prev, [currentUser]: { ...prev[currentUser], bio: value } }));
            if (!isDemo) updateUserOnServer(currentUser, { bio: value }).catch(console.error);
        }
    };

    const handlePassionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setPassion(value);
        if (updateUserData && currentUser) {
            updateUserData((prev: Record<string, UserData>) => ({ ...prev, [currentUser]: { ...prev[currentUser], passion: value } }));
            if (!isDemo) updateUserOnServer(currentUser, { passion: value }).catch(console.error);
        }
    };

    const handleVendorToggle = (checked: boolean) => {
        setIsVendor(checked);
        if (updateUserData && currentUser) {
            updateUserData((prev: Record<string, UserData>) => ({ ...prev, [currentUser]: { ...prev[currentUser], isVendor: checked } }));
            if (!isDemo) updateUserOnServer(currentUser, { isVendor: checked }).catch(console.error);
        }
    };

    const handleVendorNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const rawValue = e.target.value;
        const value = rawValue.replace(/[^a-zA-Z0-9\-]/g, '').toLowerCase();
        setVendorName(value);

        if (value.trim()) {
            const isTaken = Object.keys(usersData).some(key => {
                if (key === currentUser) return false;
                const u = usersData[key];
                return u?.isVendor && u?.vendorName?.trim().toLowerCase() === value.trim().toLowerCase();
            });

            if (isTaken) {
                setVendorError('This Vendor Name is already registered by another shop!');
            } else {
                setVendorError(null);
                if (updateUserData && currentUser) {
                    const uniqueVendorId = usersData[currentUser || '']?.vendorId || `vnd_${Date.now()}`;
                    updateUserData((prev: Record<string, UserData>) => ({
                        ...prev,
                        [currentUser || '']: { 
                            ...prev[currentUser || ''], 
                            vendorName: value,
                            vendorId: uniqueVendorId
                        }
                    }));
                    if (!isDemo && currentUser) updateUserOnServer(currentUser, { vendorName: value, vendorId: uniqueVendorId }).catch(console.error);
                }
            }
        } else {
            setVendorError(null);
        }
    };



    const handleSocialChange = (platform: string, value: string) => {
        const newSocials = { ...socialsState, [platform]: value };
        setSocialsState(newSocials);
        if (updateUserData && currentUser) {
            updateUserData((prev: Record<string, UserData>) => ({ ...prev, [currentUser]: { ...prev[currentUser], socials: newSocials } }));
            if (!isDemo) updateUserOnServer(currentUser, { socials: newSocials }).catch(console.error);
        }
    };

    const getVideoDuration = (file: File): Promise<number> => {
        return new Promise((resolve) => {
            const video = document.createElement('video');
            video.preload = 'metadata';
            video.onloadedmetadata = () => {
                window.URL.revokeObjectURL(video.src);
                resolve(video.duration);
            };
            video.onerror = () => {
                resolve(0);
            };
            video.src = URL.createObjectURL(file);
        });
    };

    const handleBgFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file && currentUser) {
            const isVideo = file.type.startsWith('video/');
            if (isVideo) {
                const duration = await getVideoDuration(file);
                if (duration > 5.1) {
                    alert("Video backgrounds must be 5 seconds or less.");
                    return;
                }
            }
            const tempUrl = URL.createObjectURL(file) + (isVideo ? "#video" : "");
            onHomeBgModeChange?.('image');
            onBgChange({ url: tempUrl, style: isVideo ? 'cover' : 'tile' }); // Default to tile to enable resolution slider if not video
            try {
                const isPreservedType = isVideo || ['image/gif', 'image/webp', 'image/apng'].includes(file.type) || /\.(gif|webp|mp4|webm|ogg|mov|m4v)$/i.test(file.name);
                let fileToUpload = file;
                if (!isPreservedType) {
                    try {
                        const compressedBlob = await compressImage(file);
                        fileToUpload = new File([compressedBlob], file.name.replace(/\.[^/.]+$/, "") + ".png", { type: 'image/png' });
                    } catch (e) {
                        console.error("Compression failed, using original", e);
                    }
                }
                const url = await uploadMediaFile(fileToUpload, currentUser);
                onBgChange({ url, style: isVideo ? 'cover' : 'tile' });
            } catch (e) { console.error("BG upload failed", e); }
        }
    };

    const handleProfileBgFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file && currentUser) {
            const isVideo = file.type.startsWith('video/');
            if (isVideo) {
                const duration = await getVideoDuration(file);
                if (duration > 5.1) {
                    alert("Video backgrounds must be 5 seconds or less.");
                    return;
                }
            }
            const tempUrl = URL.createObjectURL(file) + (isVideo ? "#video" : "");
            onProfileBgModeChange?.('image');
            onProfileBgChange?.({ url: tempUrl, style: isVideo ? 'cover' : 'tile' });
            try {
                const isPreservedType = isVideo || ['image/gif', 'image/webp', 'image/apng'].includes(file.type) || /\.(gif|webp|mp4|webm|ogg|mov|m4v)$/i.test(file.name);
                let fileToUpload = file;
                if (!isPreservedType) {
                    try {
                        const compressedBlob = await compressImage(file);
                        fileToUpload = new File([compressedBlob], file.name.replace(/\.[^/.]+$/, "") + ".png", { type: 'image/png' });
                    } catch (e) {
                        console.error("Compression failed, using original", e);
                    }
                }
                const url = await uploadMediaFile(fileToUpload, currentUser);
                onProfileBgChange?.({ url, style: isVideo ? 'cover' : 'tile' });
            } catch (e) { console.error("Profile BG upload failed", e); }
        }
    };

    const handleAppThemeColorChange = (color: string) => {
        setLocalAppThemeColor(color);
        try {
            document.documentElement.style.setProperty('--app-theme-color', color);
            let hex = color.replace('#', '');
            if (hex.length === 3) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
            const r = parseInt(hex.substring(0, 2), 16), g = parseInt(hex.substring(2, 4), 16), b = parseInt(hex.substring(4, 6), 16);
            if (!isNaN(r)) {
                document.documentElement.style.setProperty('--app-theme-rgb', `${r}, ${g}, ${b}`);
                document.documentElement.style.setProperty('--app-theme-bg', `rgba(${r}, ${g}, ${b}, ${currentOpacity / 100})`);
            }
        } catch (e) { }
        if (updateUserData && currentUser) {
            updateUserData((prev: Record<string, UserData>) => ({ ...prev, [currentUser]: { ...prev[currentUser], appThemeColor: color } }));
            if (!isDemo) updateUserOnServer(currentUser, { appThemeColor: color }).catch(console.error);
        }
    };

    const handleAppTextColorChange = (color: string) => {
        setLocalAppTextColor(color);
        try {
            document.documentElement.style.setProperty('--app-text-color', color);
        } catch (e) { }
        if (updateUserData && currentUser) {
            updateUserData((prev: Record<string, UserData>) => ({ ...prev, [currentUser]: { ...prev[currentUser], appTextColor: color } }));
            if (!isDemo) updateUserOnServer(currentUser, { appTextColor: color }).catch(console.error);
        }
    };

    const handleAppFontFamilyChange = (font: string) => {
        setLocalAppFontFamily(font);
        try {
            document.documentElement.style.setProperty('--app-font-family', font);
        } catch (e) { }
        if (updateUserData && currentUser) {
            updateUserData((prev: Record<string, UserData>) => ({ ...prev, [currentUser]: { ...prev[currentUser], appFontFamily: font } }));
            if (!isDemo) updateUserOnServer(currentUser, { appFontFamily: font }).catch(console.error);
        }
    };

    const handleAppIconChange = async (iconName: string) => {
        setLocalAppIconOverride(iconName);
        if (updateUserData && currentUser) {
            updateUserData((prev: Record<string, UserData>) => ({ ...prev, [currentUser]: { ...prev[currentUser], appIconOverride: iconName } }));
            if (!isDemo) updateUserOnServer(currentUser, { appIconOverride: iconName }).catch(console.error);
        }

        try {
            if (iconName === 'goodlyfe') {
                await AppIcon.reset({ suppressNotification: true });
                onNavigateToPage?.('home');
            } else {
                await AppIcon.change({ name: iconName, suppressNotification: true });
                if (iconName === 'yipyap') onNavigateToPage?.('messages', 'yipyap');
                if (iconName === 'communities') onNavigateToPage?.('communities');
                if (iconName === 'footprint') onNavigateToPage?.('vibeman1_footprint');
            }
            // Close the modal after navigating
            triggerExit('right');
        } catch (e) {
            console.error('Failed to change app icon:', e);
        }
    };

    const handleBgColorChange = (color: string) => {
        onBgChange({ url: color, style: 'color' });
    };

    const handleProfileBgColorChange = (color: string) => {
        onProfileBgChange?.({ url: color, style: 'color' });
    };

    const handleBroadcastFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0 || !currentUser) return;

        for (const file of files) {
            const isVideo = file.type.startsWith('video');
            const isAudio = file.type.startsWith('audio');
            const previewUrl = URL.createObjectURL(file);

            const newAttachment: any = {
                url: previewUrl,
                type: isVideo ? 'video' : (isAudio ? 'audio' : 'image'),
                file: file
            };

            setBroadcastMedia(prev => [...prev, newAttachment]);

            try {
                let fileToUpload = file;
                if (!isVideo && !isAudio) {
                    const compressedBlob = await compressImage(file);
                    fileToUpload = new File([compressedBlob], file.name.replace(/\.[^/.]+$/, "") + ".png", { type: 'image/png' });
                }
                const url = await uploadMediaFile(fileToUpload, currentUser);

                setBroadcastMedia(prev => prev.map(a => a.url === previewUrl ? { ...a, url } : a));
            } catch (err) {
                console.error("Broadcast media upload failed", err);
                setBroadcastMedia(prev => prev.filter(a => a.url !== previewUrl));
            }
        }
    };

    const parseBroadcastUrl = (input: string) => {
        if (!input) return null;
        const clean = input.trim();

        // 1. Post ID or post URL (e.g. /post/123, https://goodlyfe.net/post/123, post/123, or 123)
        const postMatch = clean.match(/(?:(?:https?:\/\/[^/]+)?\/post\/|^post\/)(\d+)/i) || (/^\d+$/.test(clean) ? [null, clean] : null);
        if (postMatch) {
            return { type: 'post', postId: postMatch[1] };
        }

        // 2. Hashtag (e.g. /hashtag/art, hashtag/art, #art)
        const hashMatch = clean.match(/(?:(?:https?:\/\/[^/]+)?\/hashtag\/|^hashtag\/|#)([\w-]+)/i);
        if (hashMatch) {
            return { type: 'hashtag', tag: hashMatch[1] };
        }

        // 3. Profile (e.g. /profile/alice, profile/alice, @alice)
        const profileMatch = clean.match(/(?:(?:https?:\/\/[^/]+)?\/profile\/|^profile\/|@)([\w.]+)/i);
        if (profileMatch) {
            return { type: 'profile', userId: profileMatch[1] };
        }

        // 4. Community detail (e.g. /community/:commId(/:channelId)?)
        const commMatch = clean.match(/(?:(?:https?:\/\/[^/]+)?\/community\/|^community\/)([^/]+)(?:\/([^/]+))?/i);
        if (commMatch) {
            return { type: 'community_detail', communityId: commMatch[1], query: commMatch[1], channelId: commMatch[2] || undefined };
        }

        // 5. Communities hub (/communities)
        if (/^(?:(?:https?:\/\/[^/]+)?\/communities|^communities\/?)$/i.test(clean)) {
            return { type: 'communities' };
        }

        // 6. Pimp My Ride / Store
        if (/^(?:(?:https?:\/\/[^/]+)?\/(?:pimp-my-ride|pimp_my_ride|store)|^pimp-my-ride|^pimp_my_ride|^store)$/i.test(clean)) {
            return { type: 'pimp_my_ride' };
        }

        // 7. Mod (e.g. /mod/:modId)
        const modMatch = clean.match(/(?:(?:https?:\/\/[^/]+)?\/mod\/|^mod\/)([\w_]+)/i);
        if (modMatch) {
            return { type: 'mod', modId: modMatch[1] };
        }

        // Fallback: single word treated as hashtag
        if (/^[a-zA-Z0-9_]+$/.test(clean)) {
            return { type: 'hashtag', tag: clean };
        }

        return null;
    };

    const handleBroadcastSubmit = async () => {
        if (!broadcastMessage.trim() && broadcastMedia.length === 0) return;
        if (!currentUser) return;

        let targetPageObj: any = undefined;
        if (broadcastTargetType === 'profile' && broadcastTargetUserId.trim()) {
            targetPageObj = { type: 'profile', userId: broadcastTargetUserId.trim().replace(/^@/, '') };
        } else if (broadcastTargetType === 'url' && broadcastUrlInput.trim()) {
            const parsed = parseBroadcastUrl(broadcastUrlInput);
            if (parsed) targetPageObj = parsed;
        } else if (broadcastTargetType === 'community_detail' && broadcastTargetCommunityId) {
            targetPageObj = {
                type: 'community_detail',
                communityId: broadcastTargetCommunityId,
                query: broadcastTargetCommunityId,
                channelId: broadcastTargetChannelId || undefined
            };
        } else if (broadcastTargetType === 'mod' && broadcastTargetModId) {
            targetPageObj = { type: 'mod', modId: broadcastTargetModId };
        }

        setIsSendingBroadcast(true);
        try {
            await sendSystemBroadcast(
                currentUser,
                broadcastMessage,
                broadcastMedia.map(({ url, type }) => ({ url, type })),
                isAutoSend,
                targetPageObj
            );
            setIsBroadcastModalOpen(false);
            setBroadcastMessage('');
            setBroadcastMedia([]);
            setIsAutoSend(false);
            setBroadcastTargetType('');
            setBroadcastTargetUserId('');
            setBroadcastUserQuery('');
            setBroadcastUrlInput('');
            setBroadcastTargetCommunityId('');
            setBroadcastTargetChannelId('');
            setBroadcastCommunityQuery('');
            setBroadcastTargetModId('');
            setBroadcastModQuery('');
            alert('System broadcast sent successfully!');
        } catch (err) {
            console.error("Failed to send broadcast", err);
            alert('Failed to send broadcast. Please try again.');
        } finally {
            setIsSendingBroadcast(false);
        }
    };

    const purchasedItems = userData?.purchasedItems || [];
    const activeCustomizations = userData?.activeCustomizations || {};
    const totalPoints = userData?.totalPoints || 0;

    const handlePurchase = async (item: any) => {
        if (totalPoints < item.price) return alert("Not enough points!");
        if (window.confirm(`Spend ${item.price} points on ${item.name}?`)) {
            const newPurchased = [...purchasedItems, item.id], newPoints = totalPoints - item.price;
            if (updateUserData && currentUser) {
                updateUserData((prev: any) => ({ ...prev, [currentUser]: { ...prev[currentUser], purchasedItems: newPurchased, totalPoints: newPoints } }));
                await updateUserOnServer(currentUser, { purchasedItems: newPurchased, totalPoints: newPoints }).catch(console.error);
            }
        }
    };

    const handleEquip = async (item: any) => {
        if (updateUserData && currentUser) {
            let updates: any;
            if (item.category === 'postcards') {
                updates = { postcardDefault: item.id };
            } else if (item.category === 'mods') {
                const currentMods = activeCustomizations?.mods || [];
                const newMods = currentMods.includes(item.id)
                    ? currentMods.filter((id: string) => id !== item.id)
                    : [...currentMods, item.id];
                updates = { activeCustomizations: { ...activeCustomizations, mods: newMods } };
            } else {
                updates = { activeCustomizations: { ...activeCustomizations, [item.category]: item.id } };
            }
            updateUserData((prev: any) => ({ ...prev, [currentUser]: { ...prev[currentUser], ...updates } }));
            if (!isDemo) await updateUserOnServer(currentUser, updates).catch(console.error);
        }
    };

    const hexToRgba = (hex: string, op: number) => {
        let c = hex.replace('#', '');
        if (c.length === 3) c = c[0] + c[0] + c[1] + c[1] + c[2] + c[2];
        const r = parseInt(c.substring(0, 2), 16), g = parseInt(c.substring(2, 4), 16), b = parseInt(c.substring(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${op / 100})`;
    };

    const handleToggleQuickAction = async (type: 'community' | 'mod', targetId: string, title?: string) => {
        const currentActions = userData?.quickActions || [];
        const index = currentActions.findIndex((a: any) => a.type === type && a.targetId === targetId);
        let newActions: any[];
        if (index > -1) {
            newActions = currentActions.filter((_: any, i: number) => i !== index);
        } else {
            newActions = [...currentActions, { type, targetId, title }];
        }

        if (updateUserData && currentUser) {
            updateUserData((prev: Record<string, UserData>) => ({
                ...prev,
                [currentUser]: { ...prev[currentUser], quickActions: newActions }
            }));
            if (!isDemo) {
                await updateUserOnServer(currentUser, { quickActions: newActions }).catch(console.error);
            }
        }

        // Let it flush then update shortcuts
        setTimeout(() => {
            updateAppShortcuts(usersData, loggedInAccounts || [currentUser || ''], communities);
        }, 100);
    };

    const sortedNotifications = (notifications as Notification[]).filter((n: Notification) => {
        const isBlocked = (currentUser && usersData[currentUser]?.blockedUsers || []).includes(n.fromUser) || (usersData[n.fromUser]?.blockedUsers || []).includes(currentUser || '');
        if (isBlocked) return false;

        if (selectedNotifFilters.length === 0) return true;

        if (selectedNotifFilters.includes('app') && n.type === 'system') return true;
        if (selectedNotifFilters.includes('follows') && ['friend', 'friend_request', 'friend_accepted', 'follow', 'follow_request', 'follow_accepted', 'link'].includes(n.type)) return true;
        if (selectedNotifFilters.includes('community') && n.type === 'community') return true;
        if (selectedNotifFilters.includes('likes') && n.type === 'like') return true;
        if (selectedNotifFilters.includes('comments') && (n.type === 'comment' || n.type === 'reply')) return true;
        if (selectedNotifFilters.includes('bumps') && n.type === 'bump') return true;
        if (selectedNotifFilters.includes('mentions') && n.type === 'mention') return true;

        return false;
    }).sort((a: Notification, b: Notification) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    const [activeStoreCategory, setActiveStoreCategory] = useState<any>('postcards');
    const [showLibraryOnly, setShowLibraryOnly] = useState(false);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center animate-fade-in p-0 md:p-8 lg:p-12">
            {/* Backdrop Overlay */}
            <div
                className="absolute inset-0"
                onClick={onClose}
            />

            <div
                ref={modalRef}
                className="relative w-full h-full md:w-[85%] lg:w-[80%] max-h-full flex flex-col overflow-hidden md:rounded-[3rem] global-settings-modal-content"
                style={{
                    color: 'var(--app-text-color)',
                    transition: isExiting ? 'transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.1)' : undefined,
                    transform: isExiting ? (exitDirection === 'left' ? 'translateX(-100vw)' : 'translateX(100vw)') : undefined
                }}
            >
                {/* Header / Tab Bar */}
                <div className="flex-shrink-0 pt-[max(6vh,calc(var(--safe-area-top,0px)+2.5rem))] md:pt-[max(1.5rem,var(--safe-area-top,0px))] px-6 pb-2">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <div className="p-2 bg-white/10 rounded-xl">
                                {activeTab === 'search' && <SearchIcon className="w-5 h-5" />}
                                {activeTab === 'notifs' && <BellIcon className="w-5 h-5" />}
                                {activeTab === 'store' && (
                                    <div className="w-6 h-6 rounded-full overflow-hidden border border-white/20">
                                        <img src="/assets/goodieboy.png" alt="" className="w-full h-full object-cover" />
                                    </div>
                                )}
                                {activeTab === 'settings' && <SettingsIcon className="w-5 h-5" />}
                            </div>
                            <h2 className="text-xl font-black uppercase tracking-tighter italic" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>{activeTab}</h2>
                        </div>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                triggerExit('right', { type: 'home' });
                            }}
                            className="p-3 hover:bg-white/10 rounded-2xl transition-all"
                            style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18), touchAction: 'manipulation' }}
                        >
                            <CloseIcon className="w-6 h-6" />
                        </button>
                    </div>

                    <div className="flex bg-black/20 p-1 rounded-2xl border">
                        {tabsList.map(tab => (
                            <button
                                key={tab}
                                onClick={() => {
                                    handleTabSwitch(tab);
                                }}
                                className={`flex-1 flex flex-col items-center justify-center py-2.5 rounded-xl transition-all relative ${activeTab === tab ? 'bg-white/10 shadow-lg' : 'hover:bg-white/5'}`}
                                style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}
                            >
                                {tab === 'search' && <SearchIcon className="w-5 h-5" />}
                                {tab === 'notifs' && (
                                    <div className="relative">
                                        <BellIcon className="w-5 h-5" />
                                        {hasUnreadNotifications && <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full border border-gray-900" />}
                                    </div>
                                )}
                                {tab === 'store' && (
                                    <div className={`w-9 h-8 rounded-full overflow-hidden border border-white/20 transition-all ${activeTab === 'store' ? 'scale-110 shadow-lg' : 'opacity-60'}`}>
                                        <img src="/assets/goodieboy.png" alt="" className="w-full h-full object-cover" />
                                    </div>
                                )}
                                {tab === 'settings' && <SettingsIcon className="w-5 h-5" />}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Scrollable Content Area */}
                <div
                    ref={contentRef}
                    className={`flex-1 overflow-y-auto ${activeTab !== 'store' ? 'px-6' : ''} pb-24 no-scrollbar allow-scroll ${isTabSwiping ? (tabSwipeDirection === 'left' ? 'page-slide-left' : 'page-slide-right') : ''}`}
                >
                    {activeTab === 'search' && (
                        <div className="space-y-6 py-4">
                            <div className="relative group">
                                <input
                                    type="text"
                                    placeholder="Search usernames, posts, tags..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full border border-white/10 focus:border-blue-500 rounded-2xl py-4 pl-12 pr-6 outline-none transition-all placeholder:text-current opacity-70 font-bold text-sm"
                                    style={{ backgroundColor: hexToRgba(localAppThemeColor, currentOpacity) }}
                                />
                                <div style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>
                                    <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 group-focus-within:text-blue-500 transition-colors" />
                                </div>
                            </div>

                            {searchQuery.trim() && (
                                <div className="space-y-8 animate-fade-in">
                                    {searchResults.length > 0 && (
                                        <section>
                                            <p className="text-[10px] font-black uppercase tracking-[0.2em] mb-4 px-1" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>People</p>
                                            <div className="space-y-3">
                                                {searchResults.map(uname => (
                                                    <button key={uname} onClick={() => { onNavigateToProfile?.(uname); }} className="w-full flex items-center gap-4 p-3 border border-white/5 rounded-2xl hover:opacity-80 transition-all text-left" style={{ backgroundColor: hexToRgba(localAppThemeColor, currentOpacity) }}>
                                                        <img src={allUsers[uname]?.pfp} className="w-10 h-10 rounded-full object-cover" />
                                                        <div>
                                                            <p className="font-black text-sm uppercase italic tracking-tighter" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>{allUsers[uname]?.displayName}</p>
                                                            <p className="text-[10px] font-bold tracking-widest" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>@{uname}</p>
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        </section>
                                    )}
                                    {hashtagResults.length > 0 && (
                                        <section>
                                            <p className="text-[10px] font-black uppercase tracking-[0.2em] mb-4 px-1" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>Tags</p>
                                            <div className="flex flex-wrap gap-2">
                                                {hashtagResults.map(tag => (
                                                    <button key={tag} onClick={() => { onNavigateToHashtag?.(tag); }} className="px-4 py-2 bg-blue-600/10 border border-blue-500/20 rounded-xl text-blue-400 font-black text-xs">#{tag}</button>
                                                ))}
                                            </div>
                                        </section>
                                    )}
                                    {postResults.length > 0 && (
                                        <section>
                                            <h3 className="font-black italic uppercase tracking-tighter text-xs mb-4 flex items-center gap-2" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>
                                                <div className="w-1 h-3 bg-blue-500 rounded-full" /> Posts
                                            </h3>
                                            <div className="space-y-3">
                                                {postResults.map(post => (
                                                    <div
                                                        key={post.id}
                                                        onClick={() => { onNavigateToPost?.(post.id); }}
                                                        className="p-4 rounded-3xl border border-white/5 transition-all cursor-pointer group"
                                                        style={{ backgroundColor: hexToRgba(localAppThemeColor, currentOpacity) }}
                                                    >
                                                        <div className="flex items-center gap-3 mb-2">
                                                            <img src={allUsers[post.author]?.pfp} className="w-6 h-6 rounded-full object-cover" />
                                                            <span className="text-[10px] font-black italic uppercase tracking-widest text-white/50" style={{ ...stickerTextStyle(0.18) }}>{post.author}</span>
                                                        </div>
                                                        <p className="text-sm font-medium line-clamp-2 opacity-80 group-hover:opacity-100 transition-opacity" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>
                                                            {post.content.text || (post.type === 'bump' ? 'Bumped a post' : `Sent ${post.type}`)}
                                                        </p>
                                                        {post.content.media && post.content.media.length > 0 && (
                                                            <div className="mt-2 text-[8px] font-bold text-blue-400 uppercase tracking-widest">Contains {post.content.media[0].type}</div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </section>
                                    )}
                                    {searchResults.length === 0 && hashtagResults.length === 0 && postResults.length === 0 && (
                                        <div className="text-center py-20 opacity-30 italic">No matches found for "{searchQuery}"</div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'notifs' && (
                        <div className="space-y-4 py-4">
                            {userData?.role === 'admin' && (
                                <button
                                    onClick={() => setIsBroadcastModalOpen(true)}
                                    className="w-full py-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl font-black uppercase tracking-[0.2em] italic text-xs shadow-lg shadow-blue-500/20 hover:scale-[1.02] active:scale-95 transition-all border border-white/10"
                                >
                                    📢 Send System Broadcast
                                </button>
                            )}

                            {/* Notification Filters */}
                            <div className="flex items-center justify-center gap-2 overflow-x-auto pb-2 scrollbar-none no-scrollbar">
                                {[
                                    { id: 'follows', icon: <UserIcon className="w-5 h-5" />, label: 'Follows', activeClass: 'bg-blue-600/10 border-blue-500/50 text-blue-400' },
                                    { id: 'community', icon: <UsersIcon className="w-5 h-5" />, label: 'Community', activeClass: 'bg-purple-600/10 border-purple-500/50 text-purple-400' },
                                    { id: 'comments', icon: <CommentIcon className="w-5 h-5" />, label: 'Comments', activeClass: 'bg-white/10 border-white/50 text-white' },
                                    { id: 'bumps', icon: <BumpIcon className="w-5 h-5" />, label: 'Bumps', activeClass: 'bg-green-600/10 border-green-500/50 text-green-400' },
                                    { id: 'mentions', icon: <AtIcon className="w-5 h-5" />, label: 'Mentions', activeClass: 'bg-orange-600/10 border-orange-500/50 text-orange-400' },
                                    { id: 'likes', icon: <span className="text-xl">❤️</span>, label: 'Likes', activeClass: 'bg-red-600/10 border-red-500/50 shadow-[0_0_15px_rgba(239,68,68,0.2)]' },
                                    { id: 'app', icon: <img src={getAppLogo()} className="w-7 h-7 rounded-full object-cover grayscale group-data-[active=true]:grayscale-0" />, label: 'App', activeClass: 'bg-blue-600/10 border-blue-500/50 text-blue-400' }
                                ].map(f => (
                                    <button
                                        key={f.id}
                                        data-group
                                        data-active={selectedNotifFilters.includes(f.id)}
                                        onClick={() => setSelectedNotifFilters(prev =>
                                            prev.includes(f.id) ? prev.filter(x => x !== f.id) : [...prev, f.id]
                                        )}
                                        className={`flex-shrink-0 group p-3 rounded-2xl transition-all border ${selectedNotifFilters.includes(f.id)
                                            ? `${f.activeClass} shadow-[0_0_15px_rgba(37,99,235,0.2)]`.replace('shadow-[0_0_15px_rgba(37,99,235,0.2)]', f.id === 'likes' ? 'shadow-[0_0_15px_rgba(239,68,68,0.2)]' : (f.id === 'bumps' ? 'shadow-[0_0_15px_rgba(34,197,94,0.2)]' : (f.id === 'mentions' ? 'shadow-[0_0_15px_rgba(249,115,22,0.2)]' : (f.id === 'community' ? 'shadow-[0_0_15px_rgba(168,85,247,0.2)]' : 'shadow-[0_0_15px_rgba(37,99,235,0.2)]'))))
                                            : 'bg-white/10 border-white/10 text-white/40 hover:bg-white/10 hover:text-white'
                                            }`}
                                        title={f.label}
                                    >
                                        <div className="group-data-[active=true]:scale-110 transition-transform">
                                            {f.icon}
                                        </div>
                                    </button>
                                ))}
                            </div>

                            {sortedNotifications.length === 0 ? (
                                <div className="text-center py-20 opacity-30 italic text-sm">No notifications yet</div>
                            ) : (
                                <div className="space-y-3">
                                    {sortedNotifications.map(notification => (
                                        <div
                                            key={notification.id}
                                            onClick={() => {
                                                const tp = typeof notification.targetPage === 'string'
                                                    ? (() => { try { return JSON.parse(notification.targetPage as any); } catch (e) { return null; } })()
                                                    : notification.targetPage;

                                                if (notification.type === 'system') {
                                                    if (tp) {
                                                        if (tp.type === 'profile' && tp.userId) {
                                                            onNavigateToProfile?.(tp.userId);
                                                        } else if (tp.type === 'post' && tp.postId) {
                                                            onNavigateToPost?.(tp.postId);
                                                        } else if (tp.type === 'communities') {
                                                            if (onNavigateToCommunities) onNavigateToCommunities();
                                                            else onNavigateToPage?.('communities');
                                                        } else if (tp.type === 'community_detail' && (tp.communityId || tp.query)) {
                                                            onNavigateToCommunityChannel?.(tp.communityId || tp.query || '', tp.channelId);
                                                        } else if (tp.type === 'mod' && tp.modId) {
                                                            if (onNavigateToMod) onNavigateToMod(tp.modId);
                                                            else onNavigateToPage?.('mod');
                                                        } else if (tp.type === 'pimp_my_ride') {
                                                            if (onNavigateToMod) onNavigateToMod('pimp_my_ride');
                                                            else onNavigateToStore?.();
                                                        } else if (tp.type === 'hashtag' && tp.tag) {
                                                            onNavigateToHashtag?.(tp.tag.replace(/^#/, ''));
                                                        }
                                                    }
                                                } else if (notification.type === 'footprint') {
                                                    onNavigateToFootprint?.();
                                                } else if (notification.type === 'link') {
                                                    onNavigateToChat?.(notification.fromUser);
                                                } else if (notification.postId) {
                                                    onNavigateToPost?.(notification.postId);
                                                } else if (notification.type === 'community' && notification.communityId) {
                                                    onNavigateToCommunityChannel?.(notification.communityId, notification.channelId);
                                                } else {
                                                    onNavigateToProfile?.(notification.fromUser);
                                                }
                                                if (!notification.read) onMarkAsRead?.(notification.id);
                                            }}
                                            className={`p-4 rounded-3xl transition-all border ${notification.type === 'system' && notification.targetPage ? 'cursor-pointer hover:border-blue-500/40' : ''} ${notification.read ? 'border-white/5' : 'bg-blue-600/10 border-blue-500/30 shadow-[0_0_20px_rgba(37,99,235,0.1)]'}`}
                                            style={notification.read ? { backgroundColor: 'var(--app-theme-bg)' } : {}}
                                        >
                                            <div className="flex items-start gap-4">
                                                <img
                                                    src={
                                                        notification.type === 'footprint'
                                                            ? '/assets/footprint_logo.png'
                                                            : notification.type === 'system'
                                                                ? '/assets/goodieboy.png'
                                                                : (notification.type === 'community' && notification.communityId)
                                                                    ? (communities.find(c => c._id === notification.communityId)?.avatar || getAppLogo())
                                                                    : (usersData[notification.fromUser]?.pfp || ((notification.fromUser === 'goodlyfe' || notification.fromUser === '@goodlyfe' || notification.fromUser === 'system' || notification.fromUser === '@system') ? '/assets/goodieboy.png' : getAppLogo()))
                                                    }
                                                    className={`w-12 h-12 rounded-full object-cover cursor-pointer hover:opacity-80 transition-opacity`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (notification.fromUser && notification.fromUser !== 'system' && notification.fromUser !== 'goodlyfe') {
                                                            onNavigateToProfile?.(notification.fromUser);
                                                        } else if (notification.type === 'system' || notification.fromUser === 'goodlyfe' || notification.fromUser === 'system') {
                                                            onNavigateToProfile?.('goodlyfe');
                                                        }
                                                    }}
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center justify-between">
                                                        <p className="text-sm font-bold leading-tight" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>
                                                            {notification.type === 'footprint'
                                                                ? 'Footprint'
                                                                : (notification.type === 'system' || notification.fromUser === 'goodlyfe' || notification.fromUser === 'system')
                                                                    ? 'GoodieBoy'
                                                                    : (usersData[notification.fromUser]?.displayName || notification.fromUser)
                                                            }
                                                        </p>
                                                        {userData?.role === 'admin' && notification.type === 'system' && (
                                                            <div className="flex items-center gap-2">
                                                                <button
                                                                    onClick={async (e) => {
                                                                        e.stopPropagation();
                                                                        try {
                                                                            const data = await toggleAutoBroadcast(notification.id, currentUser || '');
                                                                            if (data.success) {
                                                                                setAutoSendIds(prev =>
                                                                                    data.isAutoSend
                                                                                        ? [...prev, notification.id]
                                                                                        : prev.filter(id => id !== notification.id)
                                                                                );
                                                                            }
                                                                        } catch (err) {
                                                                            console.error('Failed to toggle auto-send:', err);
                                                                        }
                                                                    }}
                                                                    className={`p-2 rounded-xl transition-all border ${autoSendIds.includes(notification.id)
                                                                        ? 'bg-blue-600/20 border-blue-500/40 text-blue-400 shadow-[0_0_10px_rgba(37,99,235,0.2)]'
                                                                        : 'bg-white/5 border-white/5 text-white/20 hover:text-white hover:bg-white/10'
                                                                        }`}
                                                                    title={autoSendIds.includes(notification.id) ? "Remove from Auto-Send" : "Set to Auto-Send"}
                                                                >
                                                                    <SparklesIcon className="w-4 h-4" />
                                                                </button>

                                                                <button
                                                                    onClick={async (e) => {
                                                                        e.stopPropagation();
                                                                        const newText = window.prompt('Edit broadcast message:', notification.message);
                                                                        if (newText !== null && newText.trim() !== '') {
                                                                            try {
                                                                                const data = await editSystemBroadcast(notification.id, currentUser || '', newText.trim());
                                                                                if (data.success) {
                                                                                    alert('Broadcast updated successfully. Please refresh to see changes.');
                                                                                }
                                                                            } catch (err) {
                                                                                console.error('Failed to edit broadcast:', err);
                                                                                alert('Failed to edit broadcast.');
                                                                            }
                                                                        }
                                                                    }}
                                                                    className="p-2 hover:bg-blue-500/20 rounded-xl text-blue-400 transition-all border border-blue-500/10"
                                                                    title="Edit Broadcast"
                                                                >
                                                                    <EditIcon className="w-4 h-4" />
                                                                </button>
                                                                <button
                                                                    onClick={async (e) => {
                                                                        e.stopPropagation();
                                                                        if (window.confirm('Are you sure you want to delete this broadcast for ALL users? This cannot be undone.')) {
                                                                            try {
                                                                                const data = await deleteSystemBroadcast(notification.id, currentUser || '');
                                                                                if (data.success) {
                                                                                    alert(`Broadcast removed from ${data.removedFrom} users.`);
                                                                                }
                                                                            } catch (err) {
                                                                                console.error('Failed to remove broadcast globally:', err);
                                                                            }
                                                                        }
                                                                    }}
                                                                    className="p-2 hover:bg-red-500/20 rounded-xl text-red-400 transition-all border border-red-500/10"
                                                                    title="Delete for All Users"
                                                                >
                                                                    <TrashIcon className="w-4 h-4" />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="mt-1">
                                                        <span className="text-sm font-normal" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>
                                                            {notification.type === 'like' && 'liked your post'}
                                                            {notification.type === 'comment' && 'commented on your post'}
                                                            {notification.type === 'reply' && 'replied to your comment'}
                                                            {notification.type === 'friend' && 'became friends with you'}
                                                            {notification.type === 'friend_request' && 'sent you a friend request'}
                                                            {notification.type === 'friend_accepted' && 'accepted your friend request'}
                                                            {notification.type === 'follow' && 'started following you'}
                                                            {notification.type === 'follow_request' && 'sent you a follow request'}
                                                            {notification.type === 'follow_accepted' && 'accepted your follow request'}
                                                            {notification.type === 'bump' && 'bumped your post'}
                                                            {notification.type === 'vouch' && 'vouched your footprint. +1 Lyfe pt!'}
                                                            {notification.type === 'mention' && (notification.isDMMention ? '' : 'mentioned you')}
                                                            {notification.type === 'community' && (notification.postId ? 'posted in a community' : 'added you to a community')}
                                                            {notification.type === 'thread_comment' && 'commented on a post you commented on'}
                                                            {notification.type === 'footprint' && notification.text}
                                                            {notification.type === 'link' && (() => {
                                                                let isLoc = false;
                                                                try {
                                                                    const payload = JSON.parse(notification.message || '{}');
                                                                    isLoc = !!payload.isLocation;
                                                                } catch (e) { }
                                                                return notification.text ? `wants to link: "${notification.text}"` : (isLoc ? "sent you their location!" : "wants to link up!");
                                                            })()}
                                                        </span>
                                                        {((notification.type === 'system' && notification.message) || (notification.type === 'community' && notification.message) || notification.type === 'mention' || notification.type === 'comment' || notification.type === 'reply' || notification.type === 'thread_comment' || (notification.type === 'vouch' && notification.vouchMessage)) && (
                                                            <p className="text-sm font-medium mt-2 leading-relaxed break-words whitespace-pre-wrap" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>
                                                                {notification.type === 'system'
                                                                    ? notification.message
                                                                    : notification.type === 'community'
                                                                        ? notification.message
                                                                        : notification.type === 'vouch'
                                                                            ? notification.vouchMessage
                                                                            : (notification.type === 'comment' || notification.type === 'reply' || notification.type === 'thread_comment')
                                                                                ? (notification.commentText || notification.text)
                                                                                : (notification.isDMMention ? "wow, you're pretty popular" : (notification.text || 'Mentioned you in a post'))
                                                                }
                                                            </p>
                                                        )}
                                                    </div>
                                                    {notification.type === 'system' && notification.media && notification.media.length > 0 && (
                                                        <NotificationMediaCarousel media={notification.media} />
                                                    )}
                                                    {notification.type === 'system' && (() => {
                                                        const tp = typeof notification.targetPage === 'string'
                                                            ? (() => { try { return JSON.parse(notification.targetPage as any); } catch (e) { return null; } })()
                                                            : notification.targetPage;
                                                        if (!tp) return null;
                                                        return (
                                                            <div className="mt-2 flex items-center">
                                                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-600/20 border border-blue-500/30 text-blue-300 text-[10px] font-black uppercase tracking-wider shadow-sm hover:bg-blue-600/30 transition-all">
                                                                    <span>
                                                                        {tp.type === 'profile' && `👤 View @${tp.userId}`}
                                                                        {tp.type === 'post' && `📝 View Post #${tp.postId}`}
                                                                        {tp.type === 'communities' && `🌐 Explore Communities`}
                                                                        {tp.type === 'community_detail' && `💬 View Community Channel`}
                                                                        {tp.type === 'mod' && `⚡ Launch Mod: ${tp.modId}`}
                                                                        {tp.type === 'pimp_my_ride' && `🛍️ Visit Pimp My Ride`}
                                                                        {tp.type === 'hashtag' && `#️⃣ #${tp.tag}`}
                                                                    </span>
                                                                    <ChevronRightIcon className="w-3 h-3 ml-0.5 opacity-70" />
                                                                </span>
                                                            </div>
                                                        );
                                                    })()}
                                                    {notification.type === 'link' && (() => {
                                                        let linkImgUrl = notification.imageUrl;
                                                        try {
                                                            const payload = JSON.parse(notification.message || '{}');
                                                            if (payload.imageUrl) linkImgUrl = payload.imageUrl;
                                                        } catch (e) { }
                                                        return linkImgUrl ? (
                                                            <NotificationMediaCarousel media={[{ type: 'image', url: linkImgUrl }]} />
                                                        ) : null;
                                                    })()}
                                                    <p className="text-[10px] mt-1 uppercase font-bold" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>{formatRelativeTime(notification.timestamp)}</p>
                                                    {/* Friend request action buttons */}
                                                    {(notification.type === 'friend_request' || notification.type === 'follow_request') && (
                                                        <div className="flex gap-2 mt-3">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    window.dispatchEvent(new CustomEvent('app.acceptFollowRequest', {
                                                                        detail: { targetUser: currentUser, requester: notification.fromUser }
                                                                    }));
                                                                }}
                                                                className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-black uppercase tracking-widest rounded-full transition-all active:scale-95"
                                                            >
                                                                Accept
                                                            </button>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    window.dispatchEvent(new CustomEvent('app.rejectFollowRequest', {
                                                                        detail: { targetUser: currentUser, requester: notification.fromUser }
                                                                    }));
                                                                }}
                                                                className="px-4 py-1.5 bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white text-[10px] font-black uppercase tracking-widest rounded-full transition-all active:scale-95 border border-white/10"
                                                            >
                                                                Decline
                                                            </button>
                                                        </div>
                                                    )}
                                                    {/* Categorize button for new friends */}
                                                    {(notification.type === 'friend' || notification.type === 'follow') && (
                                                        <div className="flex gap-2 mt-3">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    window.dispatchEvent(new CustomEvent('app.openFollowerGroups', {
                                                                        detail: { targetUser: notification.fromUser }
                                                                    }));
                                                                }}
                                                                className="px-4 py-1.5 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 border border-blue-500/30 text-[10px] font-black uppercase tracking-widest rounded-full transition-all active:scale-95"
                                                            >
                                                                Categorize
                                                            </button>
                                                        </div>
                                                    )}
                                                    {notification.postId && (notification.type === 'comment' || notification.type === 'reply' || notification.type === 'thread_comment') && (
                                                        <div className="flex gap-2 mt-3">
                                                            {userData?.disabledPostNotifications?.includes(notification.postId) ? (
                                                                <button
                                                                    onClick={async (e) => {
                                                                        e.stopPropagation();
                                                                        try {
                                                                            const response = await api.post(`/users/${currentUser}/unmute-post`, { postId: notification.postId });
                                                                            if (response.data) {
                                                                                updateUserData({ disabledPostNotifications: response.data.disabledPostNotifications });
                                                                            }
                                                                        } catch (err) {
                                                                            console.error('Failed to unmute notifications:', err);
                                                                        }
                                                                    }}
                                                                    className="px-4 py-1.5 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 border border-blue-500/30 text-[10px] font-black uppercase tracking-widest rounded-full transition-all active:scale-95"
                                                                >
                                                                    🔔 Unmute Thread
                                                                </button>
                                                            ) : (
                                                                <button
                                                                    onClick={async (e) => {
                                                                        e.stopPropagation();
                                                                        try {
                                                                            const response = await api.post(`/users/${currentUser}/mute-post`, { postId: notification.postId });
                                                                            if (response.data) {
                                                                                updateUserData({ disabledPostNotifications: response.data.disabledPostNotifications });
                                                                            }
                                                                        } catch (err) {
                                                                            console.error('Failed to mute notifications:', err);
                                                                        }
                                                                    }}
                                                                    className="px-4 py-1.5 bg-red-600/20 hover:bg-red-600/30 text-red-400 border border-red-500/30 text-[10px] font-black uppercase tracking-widest rounded-full transition-all active:scale-95"
                                                                >
                                                                    🔕 Mute Thread
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                                {!notification.read && <div className="w-2 h-2 bg-blue-500 rounded-full" />}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'store' && currentUser && (
                        <div className="py-2 w-full">
                            <PimpMyRide
                                onOpenSystemModModal={onOpenSystemModModal}
                                currentUser={currentUser}
                                usersData={usersData}
                                updateUserData={updateUserData}
                                appThemeColor={localAppThemeColor}
                                currentOpacity={currentOpacity}
                                onNavigate={(target) => {
                                    if (target.type === 'mod' && target.modId === 'pimp_my_ride') return;
                                    onClose();
                                    setTimeout(() => {
                                        const event = new CustomEvent('app-navigate', { detail: target });
                                        window.dispatchEvent(event);
                                    }, 100);
                                }}
                                isDemo={isDemo}
                            />
                        </div>
                    )}

                    {activeTab === 'settings' && (
                        <div className="space-y-8 py-4">
                            <section>
                                <p className="text-[10px] font-black uppercase tracking-[0.2em] mb-3 px-1" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>You</p>
                                <div className="rounded-3xl p-4 space-y-4" style={{ backgroundColor: 'var(--app-theme-bg)', border: '1px solid rgba(255,255,255,0.05)' }}>
                                    <div className="space-y-1">
                                        <label className="text-[9px] font-bold uppercase ml-2" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>Display Name</label>
                                        <div className="relative flex items-center">
                                            <input type="text" value={displayName} onChange={handleDisplayNameChange} className="w-full bg-black/20 border border-white/5 rounded-xl py-3 pl-4 pr-20 text-sm focus:border-blue-500 outline-none" />
                                            <button
                                                onClick={(e) => {
                                                    const input = e.currentTarget.previousElementSibling as HTMLInputElement;
                                                    input?.focus();
                                                    input?.select();
                                                }}
                                                className="absolute right-2 text-[9px] font-bold uppercase bg-black/40 hover:bg-black/60 px-2 py-1.5 rounded-lg transition-colors"
                                                style={{ color: 'var(--app-text-color)' }}
                                            >
                                                Select All
                                            </button>
                                        </div>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-[9px] font-bold uppercase ml-2" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>Follower Nickname (be nice lmao)</label>
                                        <div className="relative flex items-center">
                                            <input type="text" value={followerNickname} onChange={handleFollowerNicknameChange} className="w-full bg-black/20 border border-white/5 rounded-xl py-3 pl-4 pr-20 text-sm focus:border-blue-500 outline-none" />
                                            <button
                                                onClick={(e) => {
                                                    const input = e.currentTarget.previousElementSibling as HTMLInputElement;
                                                    input?.focus();
                                                    input?.select();
                                                }}
                                                className="absolute right-2 text-[9px] font-bold uppercase bg-black/40 hover:bg-black/60 px-2 py-1.5 rounded-lg transition-colors"
                                                style={{ color: 'var(--app-text-color)' }}
                                            >
                                                Select All
                                            </button>
                                        </div>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-[9px] font-bold uppercase ml-2" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>Bio ({bio.length}/120)</label>
                                        <textarea value={bio} onChange={handleBioChange} maxLength={120} rows={2} className="w-full bg-black/20 border border-white/5 rounded-xl py-3 px-4 text-sm focus:border-blue-500 outline-none resize-none" />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-[9px] font-bold uppercase ml-2" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>Passion (Website URL)</label>
                                        <input type="text" placeholder="https://yourwebsite.com" value={passion} onChange={handlePassionChange} className="w-full bg-black/20 border border-white/5 rounded-xl py-3 px-4 text-sm focus:border-blue-500 outline-none" />
                                        {isCheckingPassion && <p className="text-[10px] text-gray-400 mt-1 ml-2 font-bold uppercase">Checking URL...</p>}
                                        {passionError && <p className="text-[10px] text-red-400 mt-1 ml-2 font-bold uppercase">{passionError}</p>}
                                    </div>
                                    <div className="flex items-center justify-between p-1">
                                        <label className="text-[9px] font-bold uppercase ml-1" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>Favorite Reaction</label>
                                        <button onClick={() => setIsEmojiPickerOpen(true)} className="w-10 h-10 flex items-center justify-center bg-black/20 border border-white/5 rounded-xl text-xl transition-all hover:bg-black/40"><ReactionDisplay emoji={localFavoriteEmoji} /></button>
                                    </div>

                                    <div className="border-t border-white/5 pt-4">
                                        <button
                                            type="button"
                                            onClick={() => setIsSocialsOpen(!isSocialsOpen)}
                                            className="w-full flex items-center justify-between py-2 text-xs font-bold uppercase ml-1 text-left"
                                            style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}
                                        >
                                            <span>Social Links & Contacts</span>
                                            <span className={`transform transition-transform duration-200 ${isSocialsOpen ? 'rotate-90' : ''}`}>➔</span>
                                        </button>

                                        {isSocialsOpen && (
                                            <div className="mt-4 space-y-6 animate-fade-in-fast">
                                                <div className="space-y-4">
                                                    <div className="flex items-center justify-between border-b border-white/5 pb-1">
                                                        <p className="text-[8px] font-bold uppercase tracking-wider text-white/50">Social Media & Authenticated Accounts</p>
                                                        <span className="text-[8px] font-black uppercase text-blue-400">OAuth Verified</span>
                                                    </div>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                        {[
                                                            { key: 'instagram', label: 'Instagram', color: 'from-pink-500 to-purple-600' },
                                                            { key: 'tiktok', label: 'TikTok', color: 'from-cyan-400 to-pink-500' },
                                                            { key: 'twitter', label: 'X (Twitter)', color: 'from-blue-400 to-blue-600' },
                                                            { key: 'threads', label: 'Threads', color: 'from-gray-700 to-black' },
                                                            { key: 'facebook', label: 'Facebook', color: 'from-blue-600 to-blue-800' },
                                                            { key: 'youtube', label: 'YouTube', color: 'from-red-500 to-red-700' },
                                                            { key: 'reddit', label: 'Reddit', color: 'from-orange-500 to-red-600' },
                                                            { key: 'pinterest', label: 'Pinterest', color: 'from-red-600 to-rose-700' }
                                                        ].map(({ key, label, color }) => {
                                                            const isConnected = !!userData?.connectedAccounts?.[key];
                                                            const connData = userData?.connectedAccounts?.[key];
                                                            return (
                                                                <div key={key} className="space-y-1 bg-black/20 p-2.5 rounded-2xl border border-white/5">
                                                                    <div className="flex items-center justify-between">
                                                                        <label className="text-[9px] font-bold uppercase text-white/70">{label}</label>
                                                                        {isConnected ? (
                                                                            <div className="flex items-center gap-1.5">
                                                                                <span className="text-[8px] font-black text-green-400 bg-green-500/10 px-2 py-0.5 rounded-full border border-green-500/20">
                                                                                    ✓ Connected
                                                                                </span>
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => {
                                                                                        const newConns = { ...(userData?.connectedAccounts || {}) };
                                                                                        delete newConns[key];
                                                                                        if (updateUserData && currentUser) {
                                                                                            updateUserData((prev: Record<string, UserData>) => ({
                                                                                                ...prev,
                                                                                                [currentUser]: {
                                                                                                    ...prev[currentUser],
                                                                                                    connectedAccounts: newConns
                                                                                                }
                                                                                            }));
                                                                                        }
                                                                                    }}
                                                                                    className="text-[8px] font-bold text-red-400/80 hover:text-red-400 hover:underline"
                                                                                >
                                                                                    Disconnect
                                                                                </button>
                                                                            </div>
                                                                        ) : (
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => {
                                                                                    // 1. Check for registered env keys
                                                                                    const clientKeys: Record<string, string | undefined> = {
                                                                                        instagram: process.env.REACT_APP_INSTAGRAM_CLIENT_ID,
                                                                                        tiktok: process.env.REACT_APP_TIKTOK_CLIENT_KEY,
                                                                                        twitter: process.env.REACT_APP_TWITTER_CLIENT_ID,
                                                                                        threads: process.env.REACT_APP_THREADS_CLIENT_ID,
                                                                                        facebook: process.env.REACT_APP_FACEBOOK_CLIENT_ID,
                                                                                        youtube: process.env.REACT_APP_GOOGLE_CLIENT_ID,
                                                                                        reddit: process.env.REACT_APP_REDDIT_CLIENT_ID,
                                                                                        pinterest: process.env.REACT_APP_PINTEREST_CLIENT_ID
                                                                                    };

                                                                                    const envKey = clientKeys[key];
                                                                                    const redirectUri = encodeURIComponent(`${window.location.origin}/oauth-callback`);
                                                                                    const stateParam = encodeURIComponent(currentUser || '');

                                                                                    if (envKey && envKey !== '' && !envKey.startsWith('GOODLYFE_')) {
                                                                                        // Live OAuth Portal Authorization
                                                                                        const oauthEndpoints: Record<string, string> = {
                                                                                            instagram: `https://api.instagram.com/oauth/authorize?client_id=${envKey}&redirect_uri=${redirectUri}&response_type=code&scope=user_profile,user_media&state=${stateParam}`,
                                                                                            tiktok: `https://www.tiktok.com/v2/auth/authorize/?client_key=${envKey}&response_type=code&scope=user.info.basic&redirect_uri=${redirectUri}&state=${stateParam}`,
                                                                                            twitter: `https://twitter.com/i/oauth2/authorize?response_type=code&client_id=${envKey}&redirect_uri=${redirectUri}&scope=tweet.read%20users.read&state=${stateParam}`,
                                                                                            threads: `https://threads.net/oauth/authorize?client_id=${envKey}&redirect_uri=${redirectUri}&response_type=code&state=${stateParam}`,
                                                                                            facebook: `https://www.facebook.com/v18.0/dialog/oauth?client_id=${envKey}&redirect_uri=${redirectUri}&state=${stateParam}`,
                                                                                            youtube: `https://accounts.google.com/o/oauth2/v2/auth?response_type=code&client_id=${envKey}&redirect_uri=${redirectUri}&scope=https://www.googleapis.com/auth/youtube.readonly&state=${stateParam}`,
                                                                                            reddit: `https://www.reddit.com/api/v1/authorize?client_id=${envKey}&response_type=code&state=${stateParam}&redirect_uri=${redirectUri}&duration=permanent&scope=identity`,
                                                                                            pinterest: `https://www.pinterest.com/oauth/?client_id=${envKey}&redirect_uri=${redirectUri}&response_type=code&state=${stateParam}`
                                                                                        };

                                                                                        const targetUrl = oauthEndpoints[key];
                                                                                        const width = 600;
                                                                                        const height = 700;
                                                                                        const left = window.screenX + (window.outerWidth - width) / 2;
                                                                                        const top = window.screenY + (window.outerHeight - height) / 2;
                                                                                        const popup = window.open(
                                                                                            targetUrl,
                                                                                            `goodlyfe_oauth_${key}`,
                                                                                            `width=${width},height=${height},left=${left},top=${top},status=no,toolbar=no,menubar=no`
                                                                                        );
                                                                                        if (popup) {
                                                                                            try { popup.focus(); } catch (e) {}
                                                                                        }
                                                                                    } else {
                                                                                        // Interactive Handle Verification (Fallback when API Keys pending)
                                                                                        const inputHandle = socialsState[key as keyof typeof socialsState] || prompt(`Enter your verified @${label} username or profile link:`);
                                                                                        if (inputHandle && inputHandle.trim()) {
                                                                                            const cleanHandle = inputHandle.trim().replace(/^@/, '');
                                                                                            const updatedConn = {
                                                                                                ...(userData?.connectedAccounts || {}),
                                                                                                [key]: {
                                                                                                    username: cleanHandle,
                                                                                                    displayName: `@${cleanHandle}`,
                                                                                                    connectedAt: new Date().toISOString(),
                                                                                                    verifiedStatus: 'Verified Account'
                                                                                                }
                                                                                            };
                                                                                            if (updateUserData && currentUser) {
                                                                                                updateUserData((prev: Record<string, UserData>) => ({
                                                                                                    ...prev,
                                                                                                    [currentUser]: {
                                                                                                        ...prev[currentUser],
                                                                                                        connectedAccounts: updatedConn,
                                                                                                        socials: { ...prev[currentUser].socials, [key]: cleanHandle }
                                                                                                    }
                                                                                                }));
                                                                                            }
                                                                                        }
                                                                                    }
                                                                                }}
                                                                                className={`text-[8px] font-black uppercase text-white px-2 py-0.5 rounded-full bg-gradient-to-r ${color} hover:opacity-90 active:scale-95 transition-all shadow-md`}
                                                                            >
                                                                                Connect OAuth
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                    <input
                                                                        type="text"
                                                                        placeholder="username"
                                                                        value={socialsState[key as keyof typeof socialsState] || ''}
                                                                        onChange={(e) => handleSocialChange(key, e.target.value)}
                                                                        className="w-full bg-black/30 border border-white/5 rounded-xl py-1.5 px-3 text-xs focus:border-blue-500 outline-none text-white"
                                                                    />
                                                                    {isConnected && (
                                                                        <p className="text-[8px] text-white/40 ml-1">
                                                                            Authenticated as <span className="text-white/80 font-bold">{connData?.displayName || connData?.username}</span>
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                        <div className="space-y-1 bg-black/20 p-2.5 rounded-2xl border border-white/5">
                                                            <label className="text-[9px] font-bold uppercase text-white/60">Discord</label>
                                                            <input type="text" placeholder="username" value={socialsState.discord || ''} onChange={(e) => handleSocialChange('discord', e.target.value)} className="w-full bg-black/30 border border-white/5 rounded-xl py-1.5 px-3 text-xs focus:border-blue-500 outline-none text-white" />
                                                        </div>
                                                        <div className="space-y-1 bg-black/20 p-2.5 rounded-2xl border border-white/5">
                                                            <label className="text-[9px] font-bold uppercase text-white/60">Snapchat</label>
                                                            <input type="text" placeholder="username" value={socialsState.snapchat || ''} onChange={(e) => handleSocialChange('snapchat', e.target.value)} className="w-full bg-black/30 border border-white/5 rounded-xl py-1.5 px-3 text-xs focus:border-blue-500 outline-none text-white" />
                                                        </div>
                                                        <div className="space-y-1 bg-black/20 p-2.5 rounded-2xl border border-white/5">
                                                            <label className="text-[9px] font-bold uppercase text-white/60">Tumblr</label>
                                                            <input type="text" placeholder="username" value={socialsState.tumblr || ''} onChange={(e) => handleSocialChange('tumblr', e.target.value)} className="w-full bg-black/30 border border-white/5 rounded-xl py-1.5 px-3 text-xs focus:border-blue-500 outline-none text-white" />
                                                        </div>
                                                        <div className="space-y-1 bg-black/20 p-2.5 rounded-2xl border border-white/5">
                                                            <label className="text-[9px] font-bold uppercase text-white/60">Bluesky</label>
                                                            <input type="text" placeholder="username" value={socialsState.bluesky || ''} onChange={(e) => handleSocialChange('bluesky', e.target.value)} className="w-full bg-black/30 border border-white/5 rounded-xl py-1.5 px-3 text-xs focus:border-blue-500 outline-none text-white" />
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="space-y-4">
                                                    <p className="text-[8px] font-bold uppercase tracking-wider text-white/50 border-b border-white/5 pb-1">Media & Music</p>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                        <div className="space-y-1">
                                                            <label className="text-[9px] font-bold uppercase ml-2 text-white/60">YouTube</label>
                                                            <input type="text" placeholder="@channel" value={socialsState.youtube || ''} onChange={(e) => handleSocialChange('youtube', e.target.value)} className="w-full bg-black/20 border border-white/5 rounded-xl py-2 px-3 text-xs focus:border-blue-500 outline-none text-white" />
                                                        </div>
                                                        <div className="space-y-1">
                                                            <label className="text-[9px] font-bold uppercase ml-2 text-white/60">Apple Music</label>
                                                            <input type="text" placeholder="profile identifier" value={socialsState.appleMusic || ''} onChange={(e) => handleSocialChange('appleMusic', e.target.value)} className="w-full bg-black/20 border border-white/5 rounded-xl py-2 px-3 text-xs focus:border-blue-500 outline-none text-white" />
                                                        </div>
                                                        <div className="space-y-1">
                                                            <label className="text-[9px] font-bold uppercase ml-2 text-white/60">SoundCloud</label>
                                                            <input type="text" placeholder="username" value={socialsState.soundcloud || ''} onChange={(e) => handleSocialChange('soundcloud', e.target.value)} className="w-full bg-black/20 border border-white/5 rounded-xl py-2 px-3 text-xs focus:border-blue-500 outline-none text-white" />
                                                        </div>
                                                        <div className="space-y-1">
                                                            <label className="text-[9px] font-bold uppercase ml-2 text-white/60">Twitch</label>
                                                            <input type="text" placeholder="username" value={socialsState.twitch || ''} onChange={(e) => handleSocialChange('twitch', e.target.value)} className="w-full bg-black/20 border border-white/5 rounded-xl py-2 px-3 text-xs focus:border-blue-500 outline-none text-white" />
                                                        </div>
                                                        <div className="space-y-1">
                                                            <label className="text-[9px] font-bold uppercase ml-2 text-white/60">Spotify</label>
                                                            <input type="text" placeholder="username" value={socialsState.spotify || ''} onChange={(e) => handleSocialChange('spotify', e.target.value)} className="w-full bg-black/20 border border-white/5 rounded-xl py-2 px-3 text-xs focus:border-blue-500 outline-none text-white" />
                                                        </div>
                                                        <div className="space-y-1">
                                                            <label className="text-[9px] font-bold uppercase ml-2 text-white/60">Kick</label>
                                                            <input type="text" placeholder="username" value={socialsState.kick || ''} onChange={(e) => handleSocialChange('kick', e.target.value)} className="w-full bg-black/20 border border-white/5 rounded-xl py-2 px-3 text-xs focus:border-blue-500 outline-none text-white" />
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="space-y-4">
                                                    <p className="text-[8px] font-bold uppercase tracking-wider text-white/50 border-b border-white/5 pb-1">Stores & Events</p>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                        <div className="space-y-1">
                                                            <label className="text-[9px] font-bold uppercase ml-2 text-white/60">Etsy</label>
                                                            <input type="text" placeholder="username" value={socialsState.etsy || ''} onChange={(e) => handleSocialChange('etsy', e.target.value)} className="w-full bg-black/20 border border-white/5 rounded-xl py-2 px-3 text-xs focus:border-blue-500 outline-none text-white" />
                                                        </div>
                                                        <div className="space-y-1">
                                                            <label className="text-[9px] font-bold uppercase ml-2 text-white/60">Eventbrite</label>
                                                            <input type="text" placeholder="username" value={socialsState.eventbrite || ''} onChange={(e) => handleSocialChange('eventbrite', e.target.value)} className="w-full bg-black/20 border border-white/5 rounded-xl py-2 px-3 text-xs focus:border-blue-500 outline-none text-white" />
                                                        </div>
                                                        <div className="space-y-1">
                                                            <label className="text-[9px] font-bold uppercase ml-2 text-white/60">Rebelity</label>
                                                            <input type="text" placeholder="username" value={socialsState.rebelity || ''} onChange={(e) => handleSocialChange('rebelity', e.target.value)} className="w-full bg-black/20 border border-white/5 rounded-xl py-2 px-3 text-xs focus:border-blue-500 outline-none text-white" />
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="space-y-4">
                                                    <p className="text-[8px] font-bold uppercase tracking-wider text-white/50 border-b border-white/5 pb-1">Payments & Contact Info</p>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                        <div className="space-y-1">
                                                            <label className="text-[9px] font-bold uppercase ml-2 text-white/60">Cash App</label>
                                                            <input type="text" placeholder="cashtag" value={socialsState.cashapp || ''} onChange={(e) => handleSocialChange('cashapp', e.target.value)} className="w-full bg-black/20 border border-white/5 rounded-xl py-2 px-3 text-xs focus:border-blue-500 outline-none text-white" />
                                                        </div>
                                                        <div className="space-y-1">
                                                            <label className="text-[9px] font-bold uppercase ml-2 text-white/60">PayPal</label>
                                                            <input type="text" placeholder="username" value={socialsState.paypal || ''} onChange={(e) => handleSocialChange('paypal', e.target.value)} className="w-full bg-black/20 border border-white/5 rounded-xl py-2 px-3 text-xs focus:border-blue-500 outline-none text-white" />
                                                        </div>
                                                        <div className="space-y-1">
                                                            <label className="text-[9px] font-bold uppercase ml-2 text-white/60">Venmo</label>
                                                            <input type="text" placeholder="username" value={socialsState.venmo || ''} onChange={(e) => handleSocialChange('venmo', e.target.value)} className="w-full bg-black/20 border border-white/5 rounded-xl py-2 px-3 text-xs focus:border-blue-500 outline-none text-white" />
                                                        </div>
                                                        <div className="space-y-1">
                                                            <label className="text-[9px] font-bold uppercase ml-2 text-white/60">WhatsApp</label>
                                                            <input type="text" placeholder="phone number" value={socialsState.whatsapp || ''} onChange={(e) => handleSocialChange('whatsapp', e.target.value)} className="w-full bg-black/20 border border-white/5 rounded-xl py-2 px-3 text-xs focus:border-blue-500 outline-none text-white" />
                                                        </div>
                                                        <div className="space-y-1">
                                                            <label className="text-[9px] font-bold uppercase ml-2 text-white/60">Phone Number (modal only)</label>
                                                            <input type="text" placeholder="phone number" value={socialsState.phone || ''} onChange={(e) => handleSocialChange('phone', e.target.value)} className="w-full bg-black/20 border border-white/5 rounded-xl py-2 px-3 text-xs focus:border-blue-500 outline-none text-white" />
                                                        </div>
                                                        <div className="space-y-1">
                                                            <label className="text-[9px] font-bold uppercase ml-2 text-white/60">Email Address (modal only)</label>
                                                            <input type="email" placeholder="email address" value={socialsState.email || ''} onChange={(e) => handleSocialChange('email', e.target.value)} className="w-full bg-black/20 border border-white/5 rounded-xl py-2 px-3 text-xs focus:border-blue-500 outline-none text-white" />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </section>

                            {(isAdmin || usersData[currentUser || '']?.role === 'admin') && (
                                <section className="space-y-3">
                                    <p className="text-[10px] font-black uppercase tracking-[0.2em] mb-1 px-1" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>Storefront</p>
                                    <div className="rounded-3xl p-5 space-y-5" style={{ backgroundColor: 'var(--app-theme-bg)', border: '1px solid rgba(255,255,255,0.05)' }}>
                                        <div className="flex items-center justify-between">
                                            <div className="space-y-1 pr-4">
                                                <label className="text-xs font-black uppercase tracking-wider block" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.15) }}>
                                                    Register as Vendor Merchant
                                                </label>
                                                <p className="text-[10px] text-white/50 leading-relaxed">
                                                    Activate vendor tools to list products, set prices, and create your dedicated "For Sale" tab.
                                                </p>
                                            </div>
                                            <button 
                                                onClick={() => handleVendorToggle(!isVendor)}
                                                className={`relative w-12 h-6 rounded-full transition-all duration-300 flex items-center p-0.5 cursor-pointer ${isVendor ? 'bg-green-500' : 'bg-white/10'}`}
                                            >
                                                <div className={`w-5 h-5 rounded-full bg-white shadow-md transform transition-transform duration-300 ${isVendor ? 'translate-x-6' : 'translate-x-0'}`} />
                                            </button>
                                        </div>

                                        {isVendor && (
                                            <div className="space-y-4 pt-3 border-t border-white/5 animate-fade-in">
                                                <div className="space-y-1.5">
                                                    <label className="text-[9px] font-bold uppercase ml-2 block" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>
                                                        Unique Vendor Shop Name (URL Safe)
                                                    </label>
                                                    <div className="relative">
                                                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-white/40 font-mono">
                                                            goodlyfe.net/store/
                                                        </span>
                                                        <input 
                                                            type="text" 
                                                            placeholder="my-cool-shop"
                                                            value={vendorName} 
                                                            onChange={handleVendorNameChange} 
                                                            className="w-full bg-black/20 border border-white/5 rounded-xl py-3.5 pl-[8.5rem] pr-4 text-sm font-mono focus:border-green-500 outline-none text-white" 
                                                        />
                                                    </div>
                                                    {vendorError ? (
                                                        <p className="text-[10px] text-red-400 mt-1 ml-2 font-bold uppercase tracking-wider">{vendorError}</p>
                                                    ) : vendorName ? (
                                                        <p className="text-[10px] text-green-400 mt-1 ml-2 font-bold uppercase tracking-wider">
                                                            Shop active at goodlyfe.net/store/{vendorName}
                                                        </p>
                                                    ) : (
                                                        <p className="text-[10px] text-yellow-400 mt-1 ml-2 font-bold uppercase tracking-wider">
                                                            Please enter a unique shop name to register
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </section>
                            )}

                            <section>
                                <p className="text-[10px] font-black uppercase tracking-[0.2em] mb-3 px-1" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>Appearance</p>
                                <div className="rounded-3xl p-4 space-y-6" style={{ backgroundColor: 'var(--app-theme-bg)', border: '1px solid rgba(255,255,255,0.05)' }}>

                                    <div className="space-y-3 pt-2">
                                        <div className="flex justify-between items-center px-1">
                                            <label className="text-[9px] font-bold uppercase" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>Select your Home Screen! (GoodLyfe, YipYap, Communities, Footprint)</label>
                                        </div>
                                        <div className="flex flex-wrap gap-3 justify-center">
                                            {['goodlyfe', 'yipyap', 'communities', 'footprint'].map(iconKey => {
                                                let previewImg = '/assets/goodieboy.png';
                                                if (iconKey === 'yipyap') previewImg = '/assets/yipyap_logo.png';
                                                if (iconKey === 'communities') previewImg = '/assets/communities_logo.png';
                                                if (iconKey === 'footprint') previewImg = '/assets/footprint_logo.png';

                                                const isActive = localAppIconOverride === iconKey;

                                                return (
                                                    <button
                                                        key={iconKey}
                                                        onClick={() => handleAppIconChange(iconKey)}
                                                        className={`w-12 h-12 rounded-2xl overflow-hidden relative border-2 transition-all ${isActive ? 'border-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.3)] scale-105' : 'border-white/10 hover:border-white/30'}`}
                                                    >
                                                        <img src={previewImg} className="w-full h-full object-cover" />
                                                        {isActive && (
                                                            <div className="absolute top-1 right-1 w-3 h-3 bg-blue-500 rounded-full flex items-center justify-center">
                                                                <CheckmarkIcon className="w-2 h-2 text-white" />
                                                            </div>
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    {/* UI Scaling Section */}
                                    <div className="space-y-4 px-2 pt-2 no-swipe">
                                        <div className="flex justify-between items-center">
                                            <div className="flex flex-col">
                                                <label className="text-[9px] font-bold uppercase" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>Pick your size</label>
                                                <p className="text-[8px] opacity-50 uppercase tracking-tighter">Affects overall size of everything in the app</p>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <div className="flex items-center gap-1">
                                                    <input
                                                        type="number"
                                                        min="50"
                                                        max="150"
                                                        value={Math.round(uiScale * 100)}
                                                        onChange={(e) => {
                                                            const val = Number(e.target.value);
                                                            if (!isNaN(val) && val >= 50 && val <= 150) {
                                                                onUiScaleChange?.(val / 100);
                                                            }
                                                        }}
                                                        onPointerDown={(e) => e.stopPropagation()}
                                                        className="w-12 bg-white/5 border border-white/10 rounded-md text-xs font-black text-blue-400 text-center py-0.5"
                                                    />
                                                    <span className="text-xs font-black text-blue-400">%</span>
                                                </div>
                                                <button
                                                    onClick={() => onUiScaleChange?.(1.0)}
                                                    className="px-2 py-1 bg-white/5 hover:bg-white/10 rounded-md text-[8px] font-black uppercase tracking-widest border border-white/5 transition-all active:scale-95"
                                                >
                                                    Reset
                                                </button>
                                            </div>
                                        </div>
                                        <input
                                            type="range"
                                            min="0.5"
                                            max="1.5"
                                            step="0.05"
                                            value={uiScale}
                                            onPointerDown={(e) => handleSliderPointerDown(e, uiScale, 0.5, 1.5)}
                                            onChange={(e) => onUiScaleChange?.(Number(e.target.value))}
                                            className="w-full h-1.5 bg-black/40 rounded-lg appearance-none cursor-pointer accent-blue-500"
                                        />
                                    </div>
                                    <div className="space-y-3">
                                        <div className="flex justify-between items-center px-1">
                                            <label className="text-[9px] font-bold uppercase" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>App Theme Color</label>
                                            <div className="flex items-center gap-2">
                                                <span className="text-[8px] font-black uppercase opacity-40 tracking-widest">All Themes</span>
                                                <button
                                                    onClick={() => onEnforceThemeColorChange?.(!enforceThemeColor)}
                                                    className={`w-7 h-4 rounded-full transition-all relative ${enforceThemeColor ? 'bg-blue-600' : 'bg-white/10'}`}
                                                >
                                                    <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-all ${enforceThemeColor ? 'right-0.5' : 'left-0.5'}`} />
                                                </button>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full border border-white/20 relative cursor-pointer shadow-lg overflow-hidden flex-shrink-0" style={{ backgroundColor: localAppThemeColor }}>
                                                <input
                                                    type="color"
                                                    value={localAppThemeColor}
                                                    onChange={(e) => handleAppThemeColorChange(e.target.value)}
                                                    className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                                                />
                                            </div>
                                            <input
                                                type="text"
                                                value={localAppThemeColor}
                                                onChange={(e) => handleAppThemeColorChange(e.target.value)}
                                                onPointerDown={(e) => e.stopPropagation()}
                                                className="flex-1 min-w-0 bg-black/20 border border-white/5 rounded-xl py-2 px-4 text-xs font-mono"
                                            />
                                            <div className="flex items-center gap-1.5 flex-shrink-0">
                                                <button
                                                    onClick={() => {
                                                        copyToClipboard(localAppThemeColor);
                                                    }}
                                                    className="p-2.5 bg-black/20 border border-white/5 rounded-xl hover:bg-black/40 transition-all active:scale-95"
                                                    title="Copy Hex"
                                                >
                                                    <CopyIcon className="w-4 h-4 opacity-60" />
                                                </button>
                                                <button
                                                    onClick={async () => {
                                                        try {
                                                            const text = await readFromClipboard();
                                                            if (text && /^#([0-9A-F]{3}){1,2}$/i.test(text)) {
                                                                handleAppThemeColorChange(text);
                                                            }
                                                        } catch (err) { }
                                                    }}
                                                    className="p-2.5 bg-black/20 border border-white/5 rounded-xl hover:bg-black/40 transition-all active:scale-95"
                                                    title="Paste Hex"
                                                >
                                                    <PasteIcon className="w-4 h-4 opacity-60" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-3 pt-4 border-t border-white/5">
                                        <div className="flex justify-between items-center px-1">
                                            <label className="text-[9px] font-bold uppercase" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>App Text Color</label>
                                            <div className="flex items-center gap-2">
                                                <span className="text-[8px] font-black uppercase opacity-40 tracking-widest">All Text</span>
                                                <button
                                                    onClick={() => onEnforceTextColorChange?.(!enforceTextColor)}
                                                    className={`w-7 h-4 rounded-full transition-all relative ${enforceTextColor ? 'bg-blue-600' : 'bg-white/10'}`}
                                                >
                                                    <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-all ${enforceTextColor ? 'right-0.5' : 'left-0.5'}`} />
                                                </button>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full border border-white/20 relative cursor-pointer shadow-lg overflow-hidden flex-shrink-0" style={{ backgroundColor: localAppTextColor }}>
                                                <input
                                                    type="color"
                                                    value={localAppTextColor}
                                                    onChange={(e) => handleAppTextColorChange(e.target.value)}
                                                    className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                                                />
                                            </div>
                                            <input
                                                type="text"
                                                value={localAppTextColor}
                                                onChange={(e) => handleAppTextColorChange(e.target.value)}
                                                onPointerDown={(e) => e.stopPropagation()}
                                                className="flex-1 min-w-0 bg-black/20 border border-white/5 rounded-xl py-2 px-4 text-xs font-mono"
                                            />
                                            <div className="flex items-center gap-1.5 flex-shrink-0">
                                                <button
                                                    onClick={() => {
                                                        copyToClipboard(localAppTextColor);
                                                    }}
                                                    className="p-2.5 bg-black/20 border border-white/5 rounded-xl hover:bg-black/40 transition-all active:scale-95"
                                                    title="Copy Hex"
                                                >
                                                    <CopyIcon className="w-4 h-4 opacity-60" />
                                                </button>
                                                <button
                                                    onClick={async () => {
                                                        try {
                                                            const text = await readFromClipboard();
                                                            if (text && /^#([0-9A-F]{3}){1,2}$/i.test(text)) {
                                                                handleAppTextColorChange(text);
                                                            }
                                                        } catch (err) { }
                                                    }}
                                                    className="p-2.5 bg-black/20 border border-white/5 rounded-xl hover:bg-black/40 transition-all active:scale-95"
                                                    title="Paste Hex"
                                                >
                                                    <PasteIcon className="w-4 h-4 opacity-60" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>


                                    <div className="space-y-3 pt-4 border-t border-white/5">
                                        <div className="flex justify-between items-center px-1">
                                            <label className="text-[9px] font-bold uppercase" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>App Text Outline</label>
                                            <div className="flex items-center gap-2">
                                                <span className="text-[8px] font-black uppercase opacity-40 tracking-widest">All Outlines</span>
                                                <button
                                                    onClick={() => onEnforceTextOutlineColorChange?.(!enforceTextOutlineColor)}
                                                    className={`w-7 h-4 rounded-full transition-all relative ${enforceTextOutlineColor ? 'bg-blue-600' : 'bg-white/10'}`}
                                                >
                                                    <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-all ${enforceTextOutlineColor ? 'right-0.5' : 'left-0.5'}`} />
                                                </button>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full border border-white/20 relative cursor-pointer shadow-lg overflow-hidden flex-shrink-0" style={{ backgroundColor: localAppTextOutlineColor || '#000000' }}>
                                                <input
                                                    type="color"
                                                    value={localAppTextOutlineColor || '#000000'}
                                                    onChange={(e) => handleAppTextOutlineColorChange(e.target.value)}
                                                    className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                                                />
                                            </div>
                                            <input
                                                type="text"
                                                placeholder="#000000"
                                                value={localAppTextOutlineColor}
                                                onChange={(e) => handleAppTextOutlineColorChange(e.target.value)}
                                                onPointerDown={(e) => e.stopPropagation()}
                                                className="flex-1 min-w-0 bg-black/20 border border-white/5 rounded-xl py-2 px-4 text-xs font-mono placeholder:opacity-50"
                                            />
                                            <div className="flex items-center gap-1.5 flex-shrink-0">
                                                <button
                                                    onClick={() => {
                                                        copyToClipboard(localAppTextOutlineColor);
                                                    }}
                                                    className="p-2.5 bg-black/20 border border-white/5 rounded-xl hover:bg-black/40 transition-all active:scale-95"
                                                    title="Copy Hex"
                                                >
                                                    <CopyIcon className="w-4 h-4 opacity-60" />
                                                </button>
                                                <button
                                                    onClick={async () => {
                                                        try {
                                                            const text = await readFromClipboard();
                                                            if (text && /^#([0-9A-F]{3}){1,2}$/i.test(text)) {
                                                                handleAppTextOutlineColorChange(text);
                                                            }
                                                        } catch (err) { }
                                                    }}
                                                    className="p-2.5 bg-black/20 border border-white/5 rounded-xl hover:bg-black/40 transition-all active:scale-95"
                                                    title="Paste Hex"
                                                >
                                                    <PasteIcon className="w-4 h-4 opacity-60" />
                                                </button>
                                                <button
                                                    onClick={() => handleAppTextOutlineColorChange('')}
                                                    className="p-2.5 bg-black/20 border border-white/5 rounded-xl hover:bg-red-500/20 hover:text-red-400 transition-all active:scale-95 text-white/40"
                                                    title="Clear Outline"
                                                >
                                                    <TrashIcon className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-3 pt-4 border-t border-white/5">
                                        <div className="flex justify-between items-center px-1">
                                            <label className="text-[9px] font-bold uppercase" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>App Font Family</label>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <CustomFontSelect 
                                                value={FONT_OPTIONS.find(f => f.family === localAppFontFamily)?.id || 'font_default'}
                                                onChange={(newId) => {
                                                    const newFont = FONT_OPTIONS.find(f => f.id === newId);
                                                    if (newFont) handleAppFontFamilyChange(newFont.family);
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-4 px-2 no-swipe">
                                        <div className="flex justify-between items-center">
                                            <label className="text-[9px] font-bold uppercase" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>Transparency</label>
                                            <div className="flex items-center gap-4">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-[8px] font-black uppercase opacity-40 tracking-widest">All Transparency</span>
                                                    <button
                                                        onClick={() => onEnforceThemeOpacityChange?.(!enforceThemeOpacity)}
                                                        className={`w-7 h-4 rounded-full transition-all relative ${enforceThemeOpacity ? 'bg-blue-600' : 'bg-white/10'}`}
                                                    >
                                                        <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-all ${enforceThemeOpacity ? 'right-0.5' : 'left-0.5'}`} />
                                                    </button>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="100"
                                                        value={currentOpacity}
                                                        onChange={(e) => {
                                                            const val = Number(e.target.value);
                                                            if (!isNaN(val) && val >= 0 && val <= 100) {
                                                                onOpacityChange(val);
                                                            }
                                                        }}
                                                        onPointerDown={(e) => e.stopPropagation()}
                                                        className="w-12 bg-white/5 border border-white/10 rounded-md text-xs font-black text-blue-400 text-center py-0.5"
                                                    />
                                                    <span className="text-xs font-black text-blue-400">%</span>
                                                </div>
                                            </div>
                                        </div>
                                        <input
                                            type="range"
                                            min="0"
                                            max="100"
                                            value={currentOpacity}
                                            onPointerDown={(e) => handleSliderPointerDown(e, currentOpacity, 0, 100)}
                                            onChange={(e) => onOpacityChange(Number(e.target.value))}
                                            className="w-full h-1.5 bg-black/40 rounded-lg appearance-none cursor-pointer accent-blue-500"
                                        />
                                    </div>


                                </div>
                            </section>
                            <section>
                                <p className="text-[10px] font-black uppercase tracking-[0.2em] mb-3 px-1" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>Backgrounds</p>
                                <div className="rounded-3xl p-4 space-y-6" style={{ backgroundColor: 'var(--app-theme-bg)', border: '1px solid rgba(255,255,255,0.05)' }}>
                                    {/* Profile Background Section */}
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between mb-2">
                                            <label className="text-[10px] font-black uppercase tracking-tight" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>Profile Background</label>
                                                <div className="flex bg-black/40 p-1 rounded-xl border border-white/5">
                                                    {(['image', 'color'] as const).map(mode => (
                                                        <button
                                                            key={mode}
                                                            onClick={() => onProfileBgModeChange?.(mode)}
                                                            className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase transition-all ${profileBgMode === mode ? 'bg-white/20 text-white' : 'text-white hover:text-white'}`}
                                                        >
                                                            {mode}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>

                                            {profileBgMode === 'image' ? (
                                                <div className="space-y-4 animate-fade-in">
                                                    <SlideshowBuilder
                                                        slideshow={activeProfileSlideshow}
                                                        onChangeSlideshow={onProfileBgSlideshowChange!}
                                                        nativeInputRef={profileNativePhotosInputRef as React.RefObject<HTMLInputElement>}
                                                        onNativeUpload={(e) => handleNativeSlideshowUpload(e, 'profile')}
                                                        onOpenTenor={() => setTenorTarget('profile')}
                                                        onOpenPhotos={(provider) => {
                                                            setPhotosTarget('profile');
                                                            setPhotosProvider(provider);
                                                            setPhotosAuthStep('login');
                                                        }}
                                                        isAdmin={isAdmin}
                                                    />
                                                </div>
                                            ) : (
                                                <div className="space-y-4 animate-fade-in">
                                                    <div className="w-full h-32 rounded-2xl border border-white/10 flex items-center justify-center relative transition-all overflow-hidden" style={{ backgroundColor: profileBgColor }}>
                                                        <input
                                                            type="color"
                                                            value={profileBgColor}
                                                            onChange={(e) => onProfileBgColorChange?.(e.target.value)}
                                                            className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                                                        />
                                                        <div className="p-3 bg-black/40 backdrop-blur-md rounded-xl border border-white/10 text-[10px] font-black uppercase tracking-widest pointer-events-none">
                                                            Change Color
                                                        </div>
                                                    </div>
                                                </div>
                                            )}



                                        {usersData[currentUser || '']?.role === 'admin' && (
                                            <div className="border-t border-white/5 pt-6 space-y-4">
                                                <div className="flex items-center justify-between mb-2">
                                                    <div className="flex flex-col">
                                                        <label className="text-[10px] font-black uppercase tracking-tight text-blue-400">Login Screen</label>
                                                        <p className="text-[8px] opacity-50 uppercase tracking-tighter">Universal background for all users</p>
                                                    </div>
                                                    <div className="flex bg-black/40 p-1 rounded-xl border border-white/5">
                                                        {(['image', 'color'] as const).map((mode) => (
                                                            <button
                                                                key={mode}
                                                                onClick={() => onLoginBgModeChange?.(mode)}
                                                                className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase transition-all ${loginBgMode === mode ? 'bg-white/20 text-white' : 'text-white hover:text-white'}`}
                                                            >
                                                                {mode}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>

                                                {loginBgMode === 'image' ? (
                                                    <div className="space-y-4 animate-fade-in">
                                                        <SlideshowBuilder
                                                            slideshow={activeLoginSlideshow}
                                                            onChangeSlideshow={onLoginBgSlideshowChange!}
                                                            nativeInputRef={loginNativePhotosInputRef as React.RefObject<HTMLInputElement>}
                                                            onNativeUpload={(e) => handleNativeSlideshowUpload(e, 'login')}
                                                            onOpenTenor={() => setTenorTarget('login')}
                                                            onOpenPhotos={(provider) => {
                                                                setPhotosTarget('login');
                                                                setPhotosProvider(provider);
                                                                setPhotosAuthStep('login');
                                                            }}
                                                            isAdmin={isAdmin}
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="space-y-4 animate-fade-in">
                                                        <div className="w-full h-32 rounded-2xl border border-white/10 flex items-center justify-center relative transition-all overflow-hidden" style={{ backgroundColor: loginBgColor }}>
                                                            <input
                                                                type="color"
                                                                value={loginBgColor}
                                                                onChange={(e) => onLoginBgColorChange?.(e.target.value)}
                                                                className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                                                            />
                                                            <div className="p-3 bg-black/40 backdrop-blur-md rounded-xl border border-white/10 text-[10px] font-black uppercase tracking-widest pointer-events-none">
                                                                Change Color
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </section>

                            <section>
                                <p className="text-[10px] font-black uppercase tracking-[0.2em] mb-3 px-1" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>Notifications</p>
                                <div className="rounded-3xl p-4 space-y-3" style={{ backgroundColor: 'var(--app-theme-bg)', border: '1px solid rgba(255,255,255,0.05)' }}>
                                    {[
                                        { label: 'Likes', key: 'likes' },
                                        { label: 'Comments', key: 'comments' },
                                        { label: 'Follows', key: 'follows' },
                                        { label: 'Vouches', key: 'vouches' },
                                        { label: 'Bumps', key: 'bumps' },
                                        { label: 'Communities', key: 'communities' },
                                        { label: 'Link Requests', key: 'linkRequests' }
                                    ].map(({ label, key }) => (
                                        <div key={key} className="flex items-center justify-between p-2">
                                            <span className="text-xs font-bold uppercase tracking-tight" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>{label}</span>
                                            <button
                                                onClick={() => {
                                                    const newSettings = { ...notificationSettings, [key]: !notificationSettings?.[key as keyof typeof notificationSettings] };
                                                    onNotificationSettingsChange?.(newSettings as any);
                                                }}
                                                className={`w-10 h-5 rounded-full transition-all relative ${notificationSettings?.[key as keyof typeof notificationSettings] ? 'bg-blue-600' : 'bg-white/10'}`}
                                            >
                                                <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${notificationSettings?.[key as keyof typeof notificationSettings] ? 'right-1' : 'left-1'}`} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </section>

                            {Capacitor.isNativePlatform() && (
                                <section>
                                    <p className="text-[10px] font-black uppercase tracking-[0.2em] mb-3 px-1" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>Quick Actions (App Icon)</p>
                                    <div className="rounded-3xl p-4 space-y-4" style={{ backgroundColor: 'var(--app-theme-bg)', border: '1px solid rgba(255,255,255,0.05)' }}>
                                        <p className="text-xs text-white/50 mb-2 font-bold leading-relaxed">
                                            Select communities or mods to appear when you long-press the GoodLyfe icon on your home screen. Selections are synced across all your logged-in profiles.
                                        </p>
                                        <div className="space-y-2 max-h-48 overflow-y-auto no-scrollbar">
                                            {communities.filter(c => c.members.includes(currentUser || '') || c.creator === currentUser || c.admins.includes(currentUser || '')).map(c => {
                                                const isActive = (userData?.quickActions || []).some((a: any) => a.type === 'community' && a.targetId === c._id);
                                                return (
                                                    <div key={c._id} className="flex items-center justify-between p-2">
                                                        <div className="flex items-center gap-3">
                                                            {c.avatar ? <img src={c.avatar} className="w-6 h-6 rounded-full object-cover" /> : <UsersIcon className="w-5 h-5 text-gray-500" />}
                                                            <span className="text-xs font-bold" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>{c.name}</span>
                                                        </div>
                                                        <button onClick={() => handleToggleQuickAction('community', c._id, c.name)} className={`w-10 h-5 rounded-full transition-all relative ${isActive ? 'bg-blue-600' : 'bg-white/10'}`}>
                                                            <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${isActive ? 'right-1' : 'left-1'}`} />
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                            {(userData?.activeCustomizations?.mods || []).map((modId: string) => {
                                                const mod = getAllMods(usersData).find((m: any) => m.id === modId) || (userData?.customMods || []).find((m: any) => m.id === modId);
                                                if (!mod) return null;
                                                const isActive = (userData?.quickActions || []).some((a: any) => a.type === 'mod' && a.targetId === mod.id);
                                                return (
                                                    <div key={mod.id} className="flex items-center justify-between p-2">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-6 h-6 rounded-full overflow-hidden flex items-center justify-center">
                                                                <ModIcon icon={mod.icon} className="w-full h-full object-cover" />
                                                            </div>
                                                            <span className="text-xs font-bold" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>{mod.name}</span>
                                                        </div>
                                                        <button onClick={() => handleToggleQuickAction('mod', mod.id, mod.name)} className={`w-10 h-5 rounded-full transition-all relative ${isActive ? 'bg-blue-600' : 'bg-white/10'}`}>
                                                            <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${isActive ? 'right-1' : 'left-1'}`} />
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </section>
                            )}

                            <section>
                                <p className="text-[10px] font-black uppercase tracking-[0.2em] mb-3 px-1" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>Feed & Privacy</p>
                                <div className="rounded-3xl p-4 space-y-4" style={{ backgroundColor: 'var(--app-theme-bg)', border: '1px solid rgba(255,255,255,0.05)' }}>
                                    <div className="flex items-center justify-between p-2">
                                        <div>
                                            <span className="text-xs font-bold uppercase tracking-tight" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>Allow Followers</span>
                                            <p className="text-[9px] opacity-60 mt-0.5">When ON, users who add you will also follow your posts while the request is pending.</p>
                                        </div>
                                        <button
                                            onClick={() => onAllowFollowersChange?.(!allowFollowers)}
                                            className={`w-10 h-5 rounded-full transition-all relative flex-shrink-0 ml-3 ${allowFollowers ? 'bg-green-500' : 'bg-white/10'}`}
                                        >
                                            <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${allowFollowers ? 'right-1' : 'left-1'}`} />
                                        </button>
                                    </div>
                                    <div className="flex items-center justify-between p-2 border-t border-white/5 pt-4">
                                        <span className="text-xs font-bold uppercase tracking-tight" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>Community Posts on Home</span>
                                        <button
                                            onClick={() => onShowCommunityPostsOnHomeFeedChange?.(!showCommunityPostsOnHomeFeed)}
                                            className={`w-10 h-5 rounded-full transition-all relative ${showCommunityPostsOnHomeFeed ? 'bg-blue-600' : 'bg-white/10'}`}
                                        >
                                            <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${showCommunityPostsOnHomeFeed ? 'right-1' : 'left-1'}`} />
                                        </button>
                                    </div>
                                    <div className="flex items-center justify-between p-2 border-t border-white/5 pt-4">
                                        <span className="text-xs font-bold uppercase tracking-tight" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>Navbar Autohide</span>
                                        <button
                                            onClick={() => onEnableNavbarAutohideChange?.(!enableNavbarAutohide)}
                                            className={`w-10 h-5 rounded-full transition-all relative ${enableNavbarAutohide ? 'bg-blue-600' : 'bg-white/10'}`}
                                        >
                                            <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${enableNavbarAutohide ? 'right-1' : 'left-1'}`} />
                                        </button>
                                    </div>
                                    <div className="flex items-center justify-between p-2 border-t border-white/5 pt-4">
                                        <div>
                                            <span className="text-xs font-bold uppercase tracking-tight" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>Sticky Featured Lyfe</span>
                                            <p className="text-[9px] opacity-60 mt-0.5">Mobile only. Pins the Featured Lyfe carousel to the top when enabled.</p>
                                        </div>
                                        <button
                                            onClick={() => onStickyFeaturedLyfeChange?.(!stickyFeaturedLyfe)}
                                            className={`w-10 h-5 rounded-full transition-all relative flex-shrink-0 ml-3 ${stickyFeaturedLyfe ? 'bg-blue-600' : 'bg-white/10'}`}
                                        >
                                            <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${stickyFeaturedLyfe ? 'right-1' : 'left-1'}`} />
                                        </button>
                                    </div>
                                    <div className="flex items-center justify-between p-2 border-t border-white/5 pt-4">
                                        <div>
                                            <span className="text-xs font-bold uppercase tracking-tight" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>Auto-Open Now Playing</span>
                                            <p className="text-[9px] opacity-60 mt-0.5">Automatically expands the music player when returning to the app while audio is playing.</p>
                                        </div>
                                        <button
                                            onClick={() => setAutoOpenNowPlaying(!autoOpenNowPlaying)}
                                            className={`w-10 h-5 rounded-full transition-all relative flex-shrink-0 ml-3 ${autoOpenNowPlaying ? 'bg-blue-600' : 'bg-white/10'}`}
                                        >
                                            <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${autoOpenNowPlaying ? 'right-1' : 'left-1'}`} />
                                        </button>
                                    </div>
                                    <div className="space-y-3 border-t border-white/5 pt-4">
                                        <label className="text-[9px] font-bold uppercase ml-2" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>Comments Visibility</label>
                                        <div className="grid grid-cols-3 gap-2 p-1 bg-black/20 rounded-2xl">
                                            {(['public', 'followers', 'private'] as const).map(option => (
                                                <button
                                                    key={option}
                                                    onClick={() => onCommentsTabVisibilityChange?.(option)}
                                                    className={`py-2 rounded-xl text-[9px] font-black uppercase transition-all ${commentsTabVisibility === option ? 'bg-white/20 text-white' : 'text-white hover:text-white'}`}
                                                >
                                                    {option}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </section>



                            <section>
                                <p className="text-[10px] font-black uppercase tracking-[0.2em] mb-3 px-1" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>Customizations</p>
                                <div className="rounded-3xl p-4 space-y-6" style={{ backgroundColor: 'var(--app-theme-bg)', border: '1px solid rgba(255,255,255,0.05)' }}>

                                    <div className="space-y-3 border-t border-white/5 pt-4">
                                        <label className="text-[9px] font-bold uppercase ml-2" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>Default Postcard Style</label>
                                        <select
                                            value={postcardDefault || 'postcard_classic'}
                                            onChange={(e) => onPostcardDefaultChange?.(e.target.value)}
                                            className="w-full bg-black/20 border border-white/5 rounded-xl py-3 px-4 text-xs font-bold focus:border-blue-500 outline-none appearance-none"
                                        >
                                            {STORE_ITEMS.filter(item =>
                                                item.category === 'postcards' &&
                                                (item.price === 0 || (usersData[currentUser || '']?.purchasedItems || []).includes(item.id))
                                            ).map(item => (
                                                <option key={item.id} value={item.id}>{item.name}</option>
                                            ))}
                                        </select>
                                        <div className="flex items-center justify-between p-2 border-t border-white/5 pt-4">
                                            <span className="text-xs font-bold uppercase tracking-tight" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>Enforce Equipped Postcard</span>
                                            <button
                                                onClick={() => onPostcardAppearanceChange?.(postcardAppearance === 'equipped' ? 'original' : 'equipped')}
                                                className={`w-10 h-5 rounded-full transition-all relative ${postcardAppearance === 'equipped' ? 'bg-blue-600' : 'bg-white/10'}`}
                                            >
                                                <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${postcardAppearance === 'equipped' ? 'right-1' : 'left-1'}`} />
                                            </button>
                                        </div>
                                        <p className="mt-1 text-[9px] italic px-2" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>Select the default style for your posts from your library.</p>
                                    </div>

                                    <div className="space-y-3 border-t border-white/5 pt-4">
                                        <label className="text-[9px] font-bold uppercase ml-2" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>Gallery Animation</label>
                                        <select
                                            value={currentAnimation}
                                            onChange={(e) => onAnimationChange(e.target.value)}
                                            className="w-full bg-black/20 border border-white/5 rounded-xl py-3 px-4 text-xs font-bold focus:border-blue-500 outline-none appearance-none"
                                        >
                                            <option value="none">None</option>
                                            <option value="slide">Slide</option>
                                            <option value="zoom">Zoom</option>
                                            <option value="fade">Fade</option>
                                        </select>
                                    </div>
                                </div>
                            </section>

                            <div className="grid gap-3 pt-4">
                                {onNavigateToAdditionalSettings && (
                                    <button onClick={onNavigateToAdditionalSettings} className="w-full py-4 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] border border-white/5 transition-all text-center" style={{ backgroundColor: hexToRgba(localAppThemeColor, currentOpacity) }}>Additional Settings</button>
                                )}
                                {isAdmin && onOpenDebug && (
                                    <button onClick={onOpenDebug} className="w-full py-4 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] border border-white/5 hover:border-blue-500/50 transition-all text-center flex items-center justify-center gap-2" style={{ backgroundColor: hexToRgba(localAppThemeColor, currentOpacity) }}>
                                        <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                                        <span>System Logs</span>
                                    </button>
                                )}
                                {isAdmin && (
                                    <button
                                        onClick={() => {
                                            onNavigateToMasterHand?.();
                                        }}
                                        className="w-full py-4 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] border border-blue-500/30 hover:border-blue-500/80 transition-all text-center flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-500/20 to-purple-600/20 text-indigo-300 shadow-lg shadow-indigo-500/10"
                                    >
                                        <SparklesIcon className="w-4 h-4 text-indigo-400 animate-pulse" />
                                        <span>Open Master Hand Studio</span>
                                    </button>
                                )}
                                {isAdmin && (
                                    <button
                                        onClick={() => {
                                            onOpenIdeaBank?.();
                                        }}
                                        className="w-full py-4 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] border border-blue-500/30 hover:border-blue-500/80 transition-all text-center flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500/20 to-purple-600/20 text-blue-300 shadow-lg shadow-blue-500/10"
                                    >
                                        <SparklesIcon className="w-4 h-4 text-blue-400 animate-pulse" />
                                        <span>Open Idea Bank</span>
                                    </button>
                                )}
                                <button onClick={onLogout} className="w-full py-4 bg-red-600/30 hover:bg-red-600/40 text-red-500 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] border border-red-500/10 transition-all flex items-center justify-center gap-2">
                                    <LogoutIcon className="w-4 h-4" />
                                    <span>{isDemo ? 'Login' : 'Logout'}</span>
                                </button>
                                <div className="pt-8 pb-4 text-center space-y-6">
                                    <div className="w-full px-4">
                                        {(currentUser === 'goodlyfe' || currentUser === 'agk' || userData?.role === 'admin') ? (
                                            <div className="flex flex-col gap-2 w-full items-center">
                                                <textarea
                                                    value={settingsStmtInput}
                                                    onChange={(e) => setSettingsStmtInput(e.target.value)}
                                                    className="w-full p-3 bg-black/30 text-white rounded-xl border border-white/10 focus:outline-none focus:ring-1 focus:ring-blue-500 text-center text-[11px] italic leading-relaxed resize-y"
                                                    placeholder="Edit settings statement..."
                                                    rows={4}
                                                    style={{ color: "var(--app-text-color)" }}
                                                />
                                                {settingsStmtInput !== currentSettingsStmt && (
                                                    <button
                                                        onClick={async () => {
                                                            try {
                                                                await updateUserOnServer('goodlyfe', { settingsStatement: settingsStmtInput });
                                                                if (updateUserData) {
                                                                    updateUserData((prev: any) => ({
                                                                        ...prev,
                                                                        goodlyfe: { ...prev.goodlyfe, settingsStatement: settingsStmtInput }
                                                                    }));
                                                                } else {
                                                                    window.location.reload();
                                                                }
                                                            } catch (e) {
                                                                console.error('Failed to update statement', e);
                                                                alert('Failed to update statement');
                                                            }
                                                        }}
                                                        className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-full transition-colors"
                                                    >
                                                        Save Statement
                                                    </button>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="text-[11px] italic text-raised leading-relaxed text-center flex-grow settings-about-statement">
                                                <FormattedText
                                                    text={currentSettingsStmt}
                                                    onNavigateToProfile={onNavigateToProfile}
                                                    onNavigateToHashtag={onNavigateToHashtag}
                                                    fontSize={11}
                                                    textColor="var(--app-text-color)"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {/*((window as any).Capacitor?.getPlatform() !== 'ios') && (
                                    <div className="flex flex-wrap justify-center gap-4">
                                        <a href="https://cash.app/$ejikthxbai" target="_blank" rel="noopener noreferrer" className="px-6 py-2 bg-[#00D632]/30 border border-[#00D632]/20 text-[#00D632] rounded-full text-[10px] font-black uppercase tracking-widest transition-all hover:bg-[#00D632]/20 shadow-lg shadow-[#00D632]/5">CashApp</a>
                                        <a href="https://venmo.com/ejiketion" target="_blank" rel="noopener noreferrer" className="px-6 py-2 bg-[#008CFF]/30 border border-[#008CFF]/20 text-[#008CFF] rounded-full text-[10px] font-black uppercase tracking-widest transition-all hover:bg-[#008CFF]/20 shadow-lg shadow-[#008CFF]/5">Venmo</a>
                                    </div>
                                )*/}

                                {currentUser !== 'free_entry' && (
                                    <div className="flex flex-wrap justify-center gap-4">
                                        <a href="https://cash.app/$ejikthxbai" target="_blank" rel="noopener noreferrer" className="px-6 py-2 bg-[#00D632]/30 border border-[#00D632]/20 text-[#00D632] rounded-full text-[10px] font-black uppercase tracking-widest transition-all hover:bg-[#00D632]/20 shadow-lg shadow-[#00D632]/5">CashApp</a>
                                        <a href="https://venmo.com/ejiketion" target="_blank" rel="noopener noreferrer" className="px-6 py-2 bg-[#008CFF]/30 border border-[#008CFF]/20 text-[#008CFF] rounded-full text-[10px] font-black uppercase tracking-widest transition-all hover:bg-[#008CFF]/20 shadow-lg shadow-[#008CFF]/5">Venmo</a>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <EmojiPickerModal
                    isOpen={isEmojiPickerOpen}
                    onClose={() => setIsEmojiPickerOpen(false)}
                    onEmojiSelect={(emoji) => {
                        setLocalFavoriteEmoji(emoji);
                        onFavoriteEmojiChange?.(emoji);
                    }}
                />
                <style>{`
                .no-scrollbar::-webkit-scrollbar { display: none; }
                .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
                input[type="range"]::-webkit-slider-thumb {
                    -webkit-appearance: none;
                    appearance: none;
                    width: 16px;
                    height: 16px;
                    background: white;
                    border-radius: 50%;
                    cursor: pointer;
                    box-shadow: 0 0 10px rgba(0,0,0,0.5);
                }
                .global-settings-modal-content button,
                .global-settings-modal-content button *,
                .global-settings-modal-content input,
                .global-settings-modal-content textarea,
                .global-settings-modal-content select,
                .global-settings-modal-content select option,
                .global-settings-modal-content .settings-about-statement,
                .global-settings-modal-content .settings-about-statement * {
                    text-shadow: -0.8px -0.8px 0 #000, 0.8px -0.8px 0 #000, -0.8px 0.8px 0 #000, 0.8px 0.8px 0 #000, 0 0 3px rgba(255,255,255,0.35) !important;
                }
            `}</style>

                {/* System Broadcast Modal Overlay */}
                {isBroadcastModalOpen && (
                    <div className="absolute inset-0 z-[110] flex items-center justify-center p-4 md:p-8">
                        <div className="absolute inset-0" onClick={() => setIsBroadcastModalOpen(false)} />
                        <div
                            className="relative w-full max-w-xl bg-gray-900/90 border border-white/10 rounded-[2.5rem] shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200"
                            style={{ backgroundColor: hexToRgba(localAppThemeColor, 95) }}
                        >
                            <div className="p-6 border-b border-white/5 flex justify-between items-center">
                                <h3 className="text-xl font-black italic uppercase tracking-tighter text-white">System Broadcast</h3>
                                <button onClick={(e) => { e.stopPropagation(); setIsBroadcastModalOpen(false); }} style={{ touchAction: 'manipulation' }} className="p-2 hover:bg-white/10 rounded-full transition-all text-white">
                                    <CloseIcon className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto allow-scroll">
                                <textarea
                                    value={broadcastMessage}
                                    onChange={(e) => setBroadcastMessage(e.target.value)}
                                    placeholder="Write your system-wide message..."
                                    className="w-full h-28 bg-black/20 border border-white/10 rounded-2xl p-4 outline-none focus:border-blue-500 transition-all resize-none text-sm font-medium text-white"
                                />

                                {broadcastMedia.length > 0 && (
                                    <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar">
                                        {broadcastMedia.map((media: any, idx: number) => (
                                            <div key={idx} className="relative flex-shrink-0 w-24 h-24 rounded-xl overflow-hidden border border-white/10">
                                                {media.type === 'video' ? (
                                                    <video src={`${media.url}#t=0.001`} className="w-full h-full object-cover" playsInline muted />
                                                ) : media.type === 'audio' ? (
                                                    <div className="w-full h-full bg-blue-500/20 flex items-center justify-center"><VolumeHighIcon className="w-6 h-6" /></div>
                                                ) : (
                                                    <img src={media.url} className="w-full h-full object-cover" />
                                                )}
                                                <button
                                                    onClick={() => setBroadcastMedia((prev: any[]) => prev.filter((_: any, i: number) => i !== idx))}
                                                    className="absolute top-1 right-1 p-1 bg-black/60 rounded-full text-white hover:bg-red-500"
                                                >
                                                    <CloseIcon className="w-3 h-3" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="flex items-center gap-3">
                                    <button
                                        onClick={() => broadcastFileInputRef.current?.click()}
                                        className="flex items-center gap-2 px-4 py-3 bg-white/5 hover:bg-white/10 rounded-2xl text-[10px] font-black uppercase tracking-widest border border-white/5 transition-all text-white"
                                    >
                                        <ImageIcon className="w-4 h-4" />
                                        Add Media
                                    </button>
                                    <input
                                        type="file"
                                        ref={broadcastFileInputRef}
                                        className="hidden"
                                        onChange={handleBroadcastFileChange}
                                        multiple
                                        accept="image/*,video/*,audio/*,.mp3,.m4a,.wav,.flac,.aac,.ogg,.opus,.wma,.m4p,.aiff,.aif,.caf"
                                    />
                                </div>

                                {/* Destination Navigation Option */}
                                <div className="p-4 bg-black/20 border border-white/10 rounded-2xl space-y-3">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-1.5">
                                            <SparklesIcon className="w-3.5 h-3.5 text-blue-400" />
                                            <label className="text-[10px] font-black uppercase tracking-widest text-white/70">
                                                Tap Action / Page Destination
                                            </label>
                                        </div>
                                        {broadcastTargetType && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setBroadcastTargetType('');
                                                    setBroadcastTargetUserId('');
                                                    setBroadcastUserQuery('');
                                                    setBroadcastUrlInput('');
                                                    setBroadcastTargetCommunityId('');
                                                    setBroadcastTargetChannelId('');
                                                    setBroadcastCommunityQuery('');
                                                    setBroadcastTargetModId('');
                                                    setBroadcastModQuery('');
                                                }}
                                                className="text-[9px] font-bold text-red-400 hover:text-red-300 uppercase tracking-wider"
                                            >
                                                Clear Target
                                            </button>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                                        {[
                                            { id: 'profile', label: '👤 Profile' },
                                            { id: 'url', label: '🔗 URL / Post / Tag' },
                                            { id: 'community_detail', label: '💬 Community' },
                                            { id: 'mod', label: '⚡ Mod' },
                                        ].map(tab => (
                                            <button
                                                key={tab.id}
                                                type="button"
                                                onClick={() => {
                                                    setBroadcastTargetType(prev => prev === tab.id ? '' : tab.id as any);
                                                }}
                                                className={`py-2 px-2.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all border ${broadcastTargetType === tab.id ? 'bg-blue-600 border-blue-400 text-white shadow-md shadow-blue-500/20' : 'bg-white/5 border-white/5 text-white/60 hover:text-white hover:bg-white/10'}`}
                                            >
                                                {tab.label}
                                            </button>
                                        ))}
                                    </div>

                                    {/* 1. Profile Target (Search Users) */}
                                    {broadcastTargetType === 'profile' && (
                                        <div className="space-y-2 pt-1 border-t border-white/5">
                                            {broadcastTargetUserId ? (
                                                <div className="flex items-center justify-between p-2.5 bg-blue-600/20 border border-blue-500/40 rounded-xl">
                                                    <div className="flex items-center gap-2.5">
                                                        <img
                                                            src={allUsers[broadcastTargetUserId]?.pfp || ((broadcastTargetUserId === 'goodlyfe' || broadcastTargetUserId === 'system') ? '/assets/goodieboy.png' : getAppLogo())}
                                                            className="w-7 h-7 rounded-full object-cover border border-white/20"
                                                            alt=""
                                                        />
                                                        <div>
                                                            <p className="text-xs font-bold text-white leading-tight">
                                                                {allUsers[broadcastTargetUserId]?.displayName || broadcastTargetUserId}
                                                            </p>
                                                            <p className="text-[10px] text-blue-300 font-semibold">@{broadcastTargetUserId}</p>
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => setBroadcastTargetUserId('')}
                                                        className="p-1 hover:bg-white/10 rounded-full text-white/70 hover:text-white"
                                                    >
                                                        <CloseIcon className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="space-y-2">
                                                    <div className="relative">
                                                        <SearchIcon className="w-3.5 h-3.5 absolute left-3 top-3 text-white/40" />
                                                        <input
                                                            type="text"
                                                            placeholder="Search users by name or @username..."
                                                            value={broadcastUserQuery}
                                                            onChange={(e) => setBroadcastUserQuery(e.target.value)}
                                                            className="w-full bg-black/30 border border-white/10 focus:border-white/30 rounded-xl py-2 pl-9 pr-3 text-xs text-white outline-none"
                                                        />
                                                    </div>
                                                    <div className="max-h-36 overflow-y-auto space-y-1 no-scrollbar allow-scroll">
                                                        {Object.keys(allUsers)
                                                            .filter(u => {
                                                                if (!broadcastUserQuery.trim()) return true;
                                                                const q = broadcastUserQuery.toLowerCase().replace(/^@/, '');
                                                                const dName = (allUsers[u]?.displayName || '').toLowerCase();
                                                                return u.toLowerCase().includes(q) || dName.includes(q);
                                                            })
                                                            .slice(0, 15)
                                                            .map(u => (
                                                                <button
                                                                    key={u}
                                                                    type="button"
                                                                    onClick={() => setBroadcastTargetUserId(u)}
                                                                    className="w-full flex items-center gap-2.5 p-2 rounded-xl bg-white/5 hover:bg-white/10 transition-all text-left group"
                                                                >
                                                                    <img
                                                                        src={allUsers[u]?.pfp || ((u === 'goodlyfe' || u === 'system') ? '/assets/goodieboy.png' : getAppLogo())}
                                                                        className="w-6 h-6 rounded-full object-cover border border-white/10"
                                                                        alt=""
                                                                    />
                                                                    <div className="flex-1 min-w-0">
                                                                        <p className="text-xs font-bold text-white truncate leading-tight group-hover:text-blue-300">
                                                                            {allUsers[u]?.displayName || u}
                                                                        </p>
                                                                        <p className="text-[9px] text-white/50 truncate">@{u}</p>
                                                                    </div>
                                                                </button>
                                                            ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* 2. URL / Link Target (Posts, Pages & Hashtags) */}
                                    {broadcastTargetType === 'url' && (
                                        <div className="space-y-2 pt-1 border-t border-white/5">
                                            <input
                                                type="text"
                                                placeholder="Paste URL, Post ID, #hashtag, /communities, etc."
                                                value={broadcastUrlInput}
                                                onChange={(e) => setBroadcastUrlInput(e.target.value)}
                                                className="w-full bg-black/30 border border-white/10 focus:border-white/30 rounded-xl py-2.5 px-3 text-xs text-white outline-none"
                                            />

                                            {/* Live Auto-Parser Status */}
                                            {broadcastUrlInput.trim() && (() => {
                                                const parsed = parseBroadcastUrl(broadcastUrlInput);
                                                return parsed ? (
                                                    <div className="flex items-center gap-2 px-3 py-1.5 bg-green-500/20 border border-green-500/40 rounded-xl text-green-300 text-[10px] font-bold">
                                                        <span>✓ Detected Destination:</span>
                                                        <span className="text-white uppercase tracking-wider">
                                                            {parsed.type === 'post' && `Post #${parsed.postId}`}
                                                            {parsed.type === 'hashtag' && `#${parsed.tag}`}
                                                            {parsed.type === 'profile' && `@${parsed.userId}`}
                                                            {parsed.type === 'communities' && `Communities Hub`}
                                                            {parsed.type === 'community_detail' && `Community: ${parsed.communityId}`}
                                                            {parsed.type === 'pimp_my_ride' && `Pimp My Ride (Store)`}
                                                            {parsed.type === 'mod' && `Mod: ${parsed.modId}`}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <div className="px-3 py-1 bg-yellow-500/10 border border-yellow-500/20 rounded-xl text-yellow-300 text-[10px]">
                                                        Enter a valid post ID, #hashtag, URL, or path.
                                                    </div>
                                                );
                                            })()}

                                            {/* Quick Shortcuts */}
                                            <div className="flex flex-wrap gap-1.5 pt-1">
                                                <button
                                                    type="button"
                                                    onClick={() => setBroadcastUrlInput('/communities')}
                                                    className="px-2 py-1 bg-white/5 hover:bg-white/10 rounded-lg text-[9px] font-bold text-white/70 hover:text-white transition-all"
                                                >
                                                    🌐 /communities
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setBroadcastUrlInput('/pimp-my-ride')}
                                                    className="px-2 py-1 bg-white/5 hover:bg-white/10 rounded-lg text-[9px] font-bold text-white/70 hover:text-white transition-all"
                                                >
                                                    🛍️ /pimp-my-ride
                                                </button>
                                                {propPosts && propPosts.slice(0, 4).map(p => (
                                                    <button
                                                        key={p.id}
                                                        type="button"
                                                        onClick={() => setBroadcastUrlInput(`/post/${p.id}`)}
                                                        className="px-2 py-1 bg-white/5 hover:bg-white/10 rounded-lg text-[9px] font-bold text-white/70 hover:text-white transition-all"
                                                    >
                                                        📝 Post #{p.id}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* 3. Community Detail Target (Search Communities) */}
                                    {broadcastTargetType === 'community_detail' && (
                                        <div className="space-y-2 pt-1 border-t border-white/5">
                                            {broadcastTargetCommunityId ? (() => {
                                                const comm = communities.find(c => (c._id === broadcastTargetCommunityId || c.name === broadcastTargetCommunityId));
                                                return (
                                                    <div className="space-y-2">
                                                        <div className="flex items-center justify-between p-2.5 bg-blue-600/20 border border-blue-500/40 rounded-xl">
                                                            <div className="flex items-center gap-2.5">
                                                                <img
                                                                    src={comm?.avatar || getAppLogo()}
                                                                    className="w-7 h-7 rounded-xl object-cover border border-white/20"
                                                                    alt=""
                                                                />
                                                                <div>
                                                                    <p className="text-xs font-bold text-white leading-tight">{comm?.name || broadcastTargetCommunityId}</p>
                                                                    <p className="text-[10px] text-blue-300 font-semibold">{comm?.channels?.length || 1} channels</p>
                                                                </div>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    setBroadcastTargetCommunityId('');
                                                                    setBroadcastTargetChannelId('');
                                                                }}
                                                                className="p-1 hover:bg-white/10 rounded-full text-white/70 hover:text-white"
                                                            >
                                                                <CloseIcon className="w-4 h-4" />
                                                            </button>
                                                        </div>

                                                        {/* Channel Selector */}
                                                        {comm?.channels && comm.channels.length > 0 && (
                                                            <div className="space-y-1">
                                                                <label className="text-[9px] font-bold uppercase tracking-wider text-white/50">Channel (Optional):</label>
                                                                <select
                                                                    value={broadcastTargetChannelId}
                                                                    onChange={(e) => setBroadcastTargetChannelId(e.target.value)}
                                                                    className="w-full bg-black/40 border border-white/10 focus:border-white/30 rounded-xl py-2 px-3 text-xs text-white outline-none cursor-pointer"
                                                                >
                                                                    <option value="">Default / Main Channel</option>
                                                                    {comm.channels.map((ch: any) => (
                                                                        <option key={ch._id || ch.name} value={ch._id || ch.name}>#{ch.name}</option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })() : (
                                                <div className="space-y-2">
                                                    <div className="relative">
                                                        <SearchIcon className="w-3.5 h-3.5 absolute left-3 top-3 text-white/40" />
                                                        <input
                                                            type="text"
                                                            placeholder="Search communities..."
                                                            value={broadcastCommunityQuery}
                                                            onChange={(e) => setBroadcastCommunityQuery(e.target.value)}
                                                            className="w-full bg-black/30 border border-white/10 focus:border-white/30 rounded-xl py-2 pl-9 pr-3 text-xs text-white outline-none"
                                                        />
                                                    </div>
                                                    <div className="max-h-36 overflow-y-auto space-y-1 no-scrollbar allow-scroll">
                                                        {communities
                                                            .filter(c => {
                                                                if (!broadcastCommunityQuery.trim()) return true;
                                                                const q = broadcastCommunityQuery.toLowerCase();
                                                                return c.name.toLowerCase().includes(q) || (c.description || '').toLowerCase().includes(q);
                                                            })
                                                            .map(c => (
                                                                <button
                                                                    key={c._id || c.name}
                                                                    type="button"
                                                                    onClick={() => {
                                                                        setBroadcastTargetCommunityId(c._id || c.name);
                                                                        setBroadcastTargetChannelId('');
                                                                    }}
                                                                    className="w-full flex items-center gap-2.5 p-2 rounded-xl bg-white/5 hover:bg-white/10 transition-all text-left group"
                                                                >
                                                                    <img
                                                                        src={c.avatar || getAppLogo()}
                                                                        className="w-6 h-6 rounded-lg object-cover border border-white/10"
                                                                        alt=""
                                                                    />
                                                                    <div className="flex-1 min-w-0">
                                                                        <p className="text-xs font-bold text-white truncate leading-tight group-hover:text-blue-300">
                                                                            {c.name}
                                                                        </p>
                                                                        {c.description && <p className="text-[9px] text-white/50 truncate">{c.description}</p>}
                                                                    </div>
                                                                </button>
                                                            ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* 4. Mod Target (Search Mods) */}
                                    {broadcastTargetType === 'mod' && (
                                        <div className="space-y-2 pt-1 border-t border-white/5">
                                            {broadcastTargetModId ? (() => {
                                                const allModsList = getAllMods(allUsers, currentUser);
                                                const mod = allModsList.find(m => m.id === broadcastTargetModId);
                                                return (
                                                    <div className="flex items-center justify-between p-2.5 bg-blue-600/20 border border-blue-500/40 rounded-xl">
                                                        <div className="flex items-center gap-2.5">
                                                            <div className="w-7 h-7 rounded-xl bg-white/10 flex items-center justify-center overflow-hidden border border-white/20">
                                                                {mod?.icon ? <ModIcon icon={mod.icon} className="w-5 h-5 object-contain" /> : <SparklesIcon className="w-4 h-4 text-blue-400" />}
                                                            </div>
                                                            <div>
                                                                <p className="text-xs font-bold text-white leading-tight">{mod?.name || broadcastTargetModId}</p>
                                                                <p className="text-[10px] text-blue-300 font-semibold">{mod?.author ? `by @${mod.author}` : mod?.id}</p>
                                                            </div>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => setBroadcastTargetModId('')}
                                                            className="p-1 hover:bg-white/10 rounded-full text-white/70 hover:text-white"
                                                        >
                                                            <CloseIcon className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                );
                                            })() : (
                                                <div className="space-y-2">
                                                    <div className="relative">
                                                        <SearchIcon className="w-3.5 h-3.5 absolute left-3 top-3 text-white/40" />
                                                        <input
                                                            type="text"
                                                            placeholder="Search mods..."
                                                            value={broadcastModQuery}
                                                            onChange={(e) => setBroadcastModQuery(e.target.value)}
                                                            className="w-full bg-black/30 border border-white/10 focus:border-white/30 rounded-xl py-2 pl-9 pr-3 text-xs text-white outline-none"
                                                        />
                                                    </div>
                                                    <div className="max-h-36 overflow-y-auto space-y-1 no-scrollbar allow-scroll">
                                                        {getAllMods(allUsers, currentUser)
                                                            .filter(m => {
                                                                if (!broadcastModQuery.trim()) return true;
                                                                const q = broadcastModQuery.toLowerCase();
                                                                return m.name.toLowerCase().includes(q) || m.id.toLowerCase().includes(q) || (m.author || '').toLowerCase().includes(q) || (m.description || '').toLowerCase().includes(q);
                                                            })
                                                            .map(m => (
                                                                <button
                                                                    key={m.id}
                                                                    type="button"
                                                                    onClick={() => setBroadcastTargetModId(m.id)}
                                                                    className="w-full flex items-center gap-2.5 p-2 rounded-xl bg-white/5 hover:bg-white/10 transition-all text-left group"
                                                                >
                                                                    <div className="w-6 h-6 rounded-lg bg-white/10 flex items-center justify-center overflow-hidden border border-white/10">
                                                                        {m.icon ? <ModIcon icon={m.icon} className="w-4 h-4 object-contain" /> : <SparklesIcon className="w-3 h-3 text-blue-400" />}
                                                                    </div>
                                                                    <div className="flex-1 min-w-0">
                                                                        <p className="text-xs font-bold text-white truncate leading-tight group-hover:text-blue-300">
                                                                            {m.name}
                                                                        </p>
                                                                        <p className="text-[9px] text-white/50 truncate">{m.author ? `by @${m.author}` : m.id}</p>
                                                                    </div>
                                                                </button>
                                                            ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="px-6 py-4 border-t border-white/5 bg-black/10">
                                <label className="flex items-center gap-3 cursor-pointer group">
                                    <div
                                        onClick={() => setIsAutoSend(!isAutoSend)}
                                        className={`w-10 h-6 rounded-full transition-all relative ${isAutoSend ? 'bg-blue-600' : 'bg-gray-700'}`}
                                    >
                                        <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${isAutoSend ? 'left-5' : 'left-1'}`} />
                                    </div>
                                    <span className="text-[10px] font-black uppercase tracking-widest text-white/60 group-hover:text-white transition-colors">
                                        Auto-send to new users
                                    </span>
                                </label>
                                <p className="text-[8px] text-white/40 mt-1 italic leading-tight">
                                    Flagged broadcasts will be delivered to every new user upon registration.
                                </p>
                            </div>

                            <div className="p-6 bg-black/20 flex gap-3">
                                <button
                                    onClick={handleBroadcastSubmit}
                                    disabled={isSendingBroadcast || (!broadcastMessage.trim() && broadcastMedia.length === 0)}
                                    className="flex-1 py-4 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-2xl font-black uppercase tracking-widest italic text-xs transition-all shadow-lg shadow-blue-500/20 text-white"
                                >
                                    {isSendingBroadcast ? 'Broadcasting...' : 'Blast It Out 🚀'}
                                </button>
                                <button
                                    onClick={() => setIsBroadcastModalOpen(false)}
                                    className="px-6 py-4 bg-white/5 hover:bg-white/10 rounded-2xl font-black uppercase tracking-widest text-xs transition-all border border-white/10 text-white"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                {/* Tenor GIF Search Modal */}
                <GifPickerModal
                    isOpen={tenorTarget !== null}
                    onClose={() => setTenorTarget(null)}
                    onSelectGif={(url, item) => {
                        const targetSlideshow = tenorTarget === 'home'
                            ? activeHomeSlideshow
                            : tenorTarget === 'profile'
                                ? activeProfileSlideshow
                                : activeLoginSlideshow;
                        const newS = {
                            ...targetSlideshow,
                            attachments: [
                                ...(targetSlideshow.attachments || []),
                                { 
                                    id: `tenor-${Date.now()}-${item?.id || 'gif'}`, 
                                    url: url, 
                                    type: 'image', 
                                    name: item?.name || 'GIF',
                                    resolution: 100,
                                    durationValue: 10,
                                    durationUnit: 'seconds',
                                    style: 'tile'
                                }
                            ]
                        };
                        if (tenorTarget === 'home') {
                            onHomeBgModeChange?.('image');
                            onHomeBgSlideshowChange?.(newS);
                        } else if (tenorTarget === 'profile') {
                            onProfileBgModeChange?.('image');
                            onProfileBgSlideshowChange?.(newS);
                        } else {
                            onLoginBgModeChange?.('image');
                            onLoginBgSlideshowChange?.(newS);
                        }
                        setTenorTarget(null);
                    }}
                    title="Search Slideshow GIFs"
                    zIndexClass="z-[150]"
                />

                {/* Apple & Google Photos Album Selector Modal */}
                {photosTarget !== null && photosProvider !== null && (
                    <div className="fixed inset-0 bg-black/85 backdrop-blur-md flex items-center justify-center z-[150] p-4 animate-fade-in" onClick={() => {
                        setPhotosTarget(null);
                        setPhotosProvider(null);
                        setPhotosAuthStep('none');
                        setSelectedPhotosAlbum(null);
                        setAlbumSelection([]);
                    }}>
                        <div className="bg-[#0e0e11] border border-white/10 rounded-[2rem] w-full max-w-sm max-h-[85vh] overflow-hidden flex flex-col shadow-2xl backdrop-blur-md" onClick={e => e.stopPropagation()}>
                            
                            {/* Header */}
                            <div className="p-5 flex items-center justify-between border-b border-white/5">
                                <div className="flex items-center gap-2">
                                    <span className="text-lg text-white">{photosProvider === 'apple' ? '' : 'G'}</span>
                                    <h3 className="text-sm font-black uppercase tracking-tight text-white">
                                        {photosProvider === 'apple' ? 'Apple Photos' : 'Google Photos'}
                                    </h3>
                                </div>
                                <button
                                    onClick={() => {
                                        setPhotosTarget(null);
                                        setPhotosProvider(null);
                                        setPhotosAuthStep('none');
                                        setSelectedPhotosAlbum(null);
                                        setAlbumSelection([]);
                                    }}
                                    className="p-1.5 hover:bg-white/10 rounded-full text-white/70"
                                >
                                    <CloseIcon className="w-4 h-4" />
                                </button>
                            </div>

                            {/* Content based on Auth Step */}
                            {photosAuthStep === 'login' ? (
                                <div className="p-6 space-y-4">
                                    <div className="text-center py-2">
                                        <p className="text-xs text-white/70">Connect your cloud account to browse and sync albums directly.</p>
                                    </div>
                                    <div className="space-y-3">
                                        <input
                                            type="text"
                                            placeholder={photosProvider === 'apple' ? "Apple ID / Email" : "Google Account email"}
                                            value={photosUsername}
                                            onChange={(e) => setPhotosUsername(e.target.value)}
                                            className="w-full bg-black/30 border border-white/10 focus:border-white/30 rounded-xl py-3 px-4 text-xs text-white outline-none"
                                        />
                                        <input
                                            type="password"
                                            placeholder="Password"
                                            value={photosPassword}
                                            onChange={(e) => setPhotosPassword(e.target.value)}
                                            className="w-full bg-black/30 border border-white/10 focus:border-white/30 rounded-xl py-3 px-4 text-xs text-white outline-none"
                                        />
                                    </div>
                                    <button
                                        onClick={() => {
                                            setPhotosConnecting(true);
                                            setTimeout(() => {
                                                setPhotosConnecting(false);
                                                setPhotosAuthStep('browsing');
                                            }, 1200);
                                        }}
                                        disabled={!photosUsername || !photosPassword || photosConnecting}
                                        className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl text-xs font-black uppercase tracking-widest transition-all"
                                    >
                                        {photosConnecting ? 'Connecting...' : 'Sign In & Connect'}
                                    </button>
                                </div>
                            ) : (
                                <div className="flex-1 flex flex-col min-h-0">
                                    {/* Album photos view */}
                                    {selectedPhotosAlbum ? (() => {
                                        const albumsList = photosProvider === 'google' ? googleAlbums : appleAlbums;
                                        const album = albumsList.find(a => a.id === selectedPhotosAlbum);
                                        return (
                                            <div className="flex-1 flex flex-col min-h-0">
                                                <div className="p-3 bg-white/5 flex items-center gap-2 border-b border-white/5">
                                                    <button onClick={() => setSelectedPhotosAlbum(null)} className="text-xs font-bold text-blue-400 hover:text-blue-300">
                                                        ← Back
                                                    </button>
                                                    <span className="text-[10px] font-black uppercase text-white/50 truncate">/ {album?.name}</span>
                                                </div>

                                                <div className="flex-1 overflow-y-auto p-4 allow-scroll">
                                                    <div className="grid grid-cols-3 gap-2">
                                                        {album?.photos.map((photoUrl, pIdx) => {
                                                            const isSelected = albumSelection.includes(photoUrl);
                                                            return (
                                                                <div
                                                                    key={pIdx}
                                                                    onClick={() => {
                                                                        if (isSelected) {
                                                                            setAlbumSelection(prev => prev.filter(url => url !== photoUrl));
                                                                        } else {
                                                                            setAlbumSelection(prev => [...prev, photoUrl]);
                                                                        }
                                                                    }}
                                                                    className={`aspect-square bg-black/40 rounded-lg overflow-hidden cursor-pointer relative border-2 ${isSelected ? 'border-blue-500' : 'border-white/5 hover:border-white/20'}`}
                                                                >
                                                                    <img src={photoUrl} className="w-full h-full object-cover" alt="" />
                                                                    {isSelected && (
                                                                        <div className="absolute top-1 right-1 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center text-[9px] font-bold text-white shadow-md">
                                                                            ✓
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>

                                                <div className="p-4 border-t border-white/5 flex gap-2 shrink-0">
                                                    <button
                                                        onClick={() => {
                                                            const targetS = photosTarget === 'home' 
                                                                ? activeHomeSlideshow 
                                                                : photosTarget === 'profile'
                                                                    ? activeProfileSlideshow
                                                                    : activeLoginSlideshow;
                                                            const newAttachments = [...(targetS.attachments || [])];
                                                            albumSelection.forEach((url, sIdx) => {
                                                                newAttachments.push({
                                                                    id: `cloud-${photosProvider}-${Date.now()}-${sIdx}`,
                                                                    url: url,
                                                                    type: 'image',
                                                                    name: `${photosProvider === 'apple' ? 'iCloud' : 'Google'} Photo`,
                                                                    resolution: 100,
                                                                    durationValue: 10,
                                                                    durationUnit: 'seconds',
                                                                    style: 'tile'
                                                                });
                                                            });
                                                            const newS = { ...targetS, attachments: newAttachments };
                                                            if (photosTarget === 'home') {
                                                                onHomeBgModeChange?.('image');
                                                                onHomeBgSlideshowChange?.(newS);
                                                            } else if (photosTarget === 'profile') {
                                                                onProfileBgModeChange?.('image');
                                                                onProfileBgSlideshowChange?.(newS);
                                                            } else {
                                                                onLoginBgModeChange?.('image');
                                                                onLoginBgSlideshowChange?.(newS);
                                                            }
                                                            setPhotosTarget(null);
                                                            setPhotosProvider(null);
                                                            setPhotosAuthStep('none');
                                                            setSelectedPhotosAlbum(null);
                                                            setAlbumSelection([]);
                                                        }}
                                                        disabled={albumSelection.length === 0}
                                                        className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl text-xs font-black uppercase tracking-widest transition-all"
                                                    >
                                                        Add {albumSelection.length} Photos
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })() : (
                                        /* Albums list view */
                                        <div className="flex-1 overflow-y-auto p-4 space-y-3 allow-scroll">
                                            <p className="text-[10px] uppercase tracking-wider text-white/50 mb-1">Browse Albums</p>
                                            {(photosProvider === 'google' ? googleAlbums : appleAlbums).map((album) => (
                                                <div
                                                    key={album.id}
                                                    onClick={() => setSelectedPhotosAlbum(album.id)}
                                                    className="flex items-center gap-3 p-2 bg-black/20 hover:bg-black/40 border border-white/5 hover:border-white/10 rounded-2xl cursor-pointer transition-all"
                                                >
                                                    <div className="w-12 h-12 rounded-xl overflow-hidden border border-white/10 shrink-0 bg-black/40">
                                                        <img src={album.photos[0]} className="w-full h-full object-cover" alt="" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-xs font-bold text-white truncate">{album.name}</p>
                                                        <p className="text-[9px] text-white/50 uppercase font-medium">{album.count} photos</p>
                                                    </div>
                                                    <span className="text-white/40 text-xs pr-1">➔</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export interface AdditionalSettingsModalProps {
    isOpen: boolean;
    onClose: () => void;
    username: string;
    isGuest?: boolean;
    onLogout: () => void;
    usersData: Record<string, UserData>;
    onUnblockUser: (userId: string) => Promise<void>;
}

export const AdditionalSettingsModal = ({
    isOpen,
    onClose,
    username,
    isGuest,
    onLogout,
    usersData,
    onUnblockUser
}: AdditionalSettingsModalProps) => {
    const [password, setPassword] = useState('');
    const [newEmail, setNewEmail] = useState('');
    const [newUsername, setNewUsername] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);
    const [isDeactivating, setIsDeactivating] = useState(false);
    const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
    const [isClaiming, setIsClaiming] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { currentUser } = useAuth();
    const rawName = (currentUser || username || '').replace(/^@/, '').trim();
    const resolvedUserKey = Object.keys(usersData).find(k => k.toLowerCase() === rawName.toLowerCase()) || rawName;
    const currentUserData = usersData[resolvedUserKey] || {};
    const userEmail = currentUserData?.email || currentUserData?.socials?.email || '';
    const userPhone = currentUserData?.phone || currentUserData?.socials?.phone || '';
    const userThemeColor = currentUserData?.appThemeColor || '#3b82f6';
    const userTextColor = currentUserData?.appTextColor || '#ffffff';
    const userOpacity = (currentUserData?.postBlurOpacity !== undefined ? currentUserData.postBlurOpacity : 50) / 100;
    const stickerTextStyle = (whiteOpacity: number = 0.35, textColor?: string) => {
        return baseStickerTextStyle(whiteOpacity, textColor || userTextColor);
    };

    const modalBg = `rgba(${parseInt(userThemeColor.replace('#', '').substring(0, 2), 16)}, ${parseInt(userThemeColor.replace('#', '').substring(2, 4), 16)}, ${parseInt(userThemeColor.replace('#', '').substring(4, 6), 16)}, ${userOpacity})`;

    const handleUnblockClick = async (targetId: string) => {
        try {
            await onUnblockUser(targetId);
        } catch (e) {
            console.error(e);
            setError('Failed to unblock user.');
        }
    };

    const handleUpdateEmail = async () => {
        if (!password) {
            setError('Please enter your password to confirm.');
            return;
        }
        if (!newEmail || !newEmail.includes('@')) {
            setError('Please enter a valid email address.');
            return;
        }
        setIsUpdatingEmail(true);
        setError(null);
        try {
            await updateEmail(resolvedUserKey, password, newEmail);
            if (usersData[resolvedUserKey]) {
                usersData[resolvedUserKey].email = newEmail;
            }
            alert('Email updated successfully!');
            setNewEmail('');
            setPassword('');
        } catch (err: any) {
            setError(err.response?.data?.error || 'Email update failed.');
        } finally {
            setIsUpdatingEmail(false);
        }
    };

    const handleClaimAccount = async () => {
        if (!newUsername || !newEmail || !password) {
            setError('Please fill out all fields.');
            return;
        }
        setIsClaiming(true);
        setError(null);
        try {
            // Need to import claimGuestAccount
            const { claimGuestAccount } = require('../api');
            await claimGuestAccount({ guestUsername: username, newUsername, newEmail, newPassword: password });
            alert('Account successfully claimed! Please log in with your new credentials.');
            onLogout();
        } catch (err: any) {
            setError(err.response?.data?.error || 'Account claim failed.');
        } finally {
            setIsClaiming(false);
        }
    };

    const handleDeactivate = async () => {
        if (!password) {
            setError('Please enter your password to confirm.');
            return;
        }
        setIsDeactivating(true);
        setError(null);
        try {
            await deactivateAccount(username, password);
            alert('Account deactivated. Logging you out.');
            onLogout();
        } catch (err: any) {
            setError(err.response?.data?.error || 'Deactivation failed.');
        } finally {
            setIsDeactivating(false);
        }
    };

    const handleDelete = async () => {
        if (!password) {
            setError('Please enter your password to confirm.');
            return;
        }
        if (!window.confirm('WARNING: This will permanently delete your account and ALL your data. This cannot be undone. Are you sure?')) {
            return;
        }
        setIsDeleting(true);
        setError(null);
        try {
            await deleteAccountPermanently(username, password);
            alert('Account deleted successfully. Logging you out.');
            onLogout();
        } finally {
            setIsDeleting(false);
        }
    };
    const [showPassword, setShowPassword] = useState(false);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 flex items-center justify-center z-[80] p-4 animate-fade-in bg-black/80 backdrop-blur-sm" onClick={onClose}>
            <div className="rounded-[2.5rem] w-full max-w-md overflow-hidden flex flex-col p-8 backdrop-blur-md border border-white/10 shadow-2xl" style={{ backgroundColor: modalBg, color: userTextColor }} onClick={e => e.stopPropagation()}>
                <div className="flex flex-col mb-6">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-bold" style={{ color: userThemeColor, ...stickerTextStyle(0.15) }}>{isGuest ? 'Claim Account' : 'Additional Settings'}</h2>
                        <button onClick={(e) => { e.stopPropagation(); onClose(); }} style={{ touchAction: 'manipulation' }} className="p-2 hover:bg-white/10 rounded-full text-white">
                            <CloseIcon className="w-5 h-5" />
                        </button>
                    </div>
                    {!isGuest && (
                        <div className="mt-3 p-3 bg-black/20 border border-white/10 rounded-2xl space-y-1">
                            <div className="flex items-center justify-between text-xs">
                                <span className="text-gray-400 font-medium">User:</span>
                                <span className="font-bold text-white tracking-wide">{resolvedUserKey || currentUser || username || 'Unknown'}</span>
                            </div>
                            {userEmail ? (
                                <div className="flex items-center justify-between text-xs">
                                    <span className="text-gray-400 font-medium">Email:</span>
                                    <span className="font-bold text-white tracking-wide">{userEmail}</span>
                                </div>
                            ) : (
                                <div className="flex items-center justify-between text-xs">
                                    <span className="text-gray-400 font-medium">Email:</span>
                                    <span className="text-gray-500 italic">Not set</span>
                                </div>
                            )}
                            {userPhone ? (
                                <div className="flex items-center justify-between text-xs">
                                    <span className="text-gray-400 font-medium">Phone:</span>
                                    <span className="font-bold text-white tracking-wide">{userPhone}</span>
                                </div>
                            ) : (
                                <div className="flex items-center justify-between text-xs">
                                    <span className="text-gray-400 font-medium">Phone:</span>
                                    <span className="text-gray-500 italic">Not set</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {isGuest ? (
                    <div className="space-y-4">
                        <p className="text-sm mb-4 text-gray-400">Upgrade your guest account to save your progress permanently.</p>
                        <form onSubmit={(e) => { e.preventDefault(); handleClaimAccount(); }} className="space-y-4">
                            <input
                                type="text"
                                placeholder="New Username"
                                value={newUsername}
                                onChange={(e) => setNewUsername(e.target.value)}
                                className="w-full bg-black/30 border border-white/10 focus:border-white/30 rounded-2xl p-3 text-white focus:outline-none transition-colors"
                            />
                            <input
                                type="email"
                                placeholder="Email address"
                                value={newEmail}
                                onChange={(e) => setNewEmail(e.target.value)}
                                className="w-full bg-black/30 border border-white/10 focus:border-white/30 rounded-2xl p-3 text-white focus:outline-none transition-colors"
                            />
                            <div className="relative">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    placeholder="Password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full bg-black/30 border border-white/10 focus:border-white/30 rounded-2xl p-3 text-white focus:outline-none transition-colors pr-10"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(prev => !prev)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors"
                                >
                                    {showPassword ? <EyeOffIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                                </button>
                            </div>
                            {error && <p className="text-red-400 text-xs mt-2">{error}</p>}
                            <button
                                type="submit"
                                disabled={isClaiming || !newUsername || !newEmail || !password}
                                className="w-full py-3 bg-blue-600 hover:bg-blue-500 rounded-2xl text-white font-semibold transition-colors disabled:opacity-50"
                            >
                                {isClaiming ? 'Creating Account...' : 'Save Account'}
                            </button>
                        </form>
                    </div>
                ) : (
                    <div className="space-y-6 max-h-[65vh] overflow-y-auto allow-scroll pr-1">
                        <form onSubmit={(e) => { e.preventDefault(); handleUpdateEmail(); }} className="space-y-4">
                            <p className="text-xs font-semibold mb-1 uppercase px-1" style={{ color: userThemeColor, ...stickerTextStyle(0.15) }}>Update Associated Email</p>
                            
                            <div>
                                <label className="text-[10px] text-gray-400 font-medium px-1 mb-1 block">Account Password</label>
                                <div className="relative">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        name="password"
                                        autoComplete="current-password"
                                        placeholder="Confirm account password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full bg-black/30 border border-white/10 focus:border-white/30 rounded-2xl p-3 text-white focus:outline-none transition-colors pr-10 text-sm"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(prev => !prev)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors"
                                    >
                                        {showPassword ? <EyeOffIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>

                            <div>
                                <label className="text-[10px] text-gray-400 font-medium px-1 mb-1 block">New Email Address</label>
                                <div className="relative">
                                    <input
                                        type="email"
                                        placeholder="Enter new email address"
                                        value={newEmail}
                                        onChange={(e) => setNewEmail(e.target.value)}
                                        className="w-full bg-black/30 border border-white/10 focus:border-white/30 rounded-2xl p-3 text-white focus:outline-none transition-colors pr-12 text-sm"
                                    />
                                    <button
                                        type="submit"
                                        disabled={isUpdatingEmail || !newEmail || !password}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl transition-all disabled:opacity-30 disabled:hover:bg-blue-600"
                                        title="Save New Email"
                                    >
                                        {isUpdatingEmail ? (
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        ) : (
                                            <SendIcon className="w-4 h-4" />
                                        )}
                                    </button>
                                </div>
                            </div>

                            {error && <p className="text-red-400 text-xs mt-1 px-1 font-semibold">{error}</p>}
                        </form>

                        {/* Blocked Users Section */}
                        <div className="pt-2 border-t border-white/5">
                            <p className="text-xs font-semibold mb-2 uppercase px-1" style={{ color: userThemeColor, ...stickerTextStyle(0.15) }}>Blocked Users</p>
                            {(() => {
                                const blockedList = currentUserData?.blockedUsers || [];
                                if (blockedList.length === 0) {
                                    return (
                                        <p className="text-xs px-1 opacity-60 italic" style={stickerTextStyle(0.18)}>No blocked users.</p>
                                    );
                                }
                                return (
                                    <div className="space-y-2 bg-black/20 border border-white/5 rounded-2xl p-3 max-h-40 overflow-y-auto allow-scroll">
                                        {blockedList.map((blockedUserId: string) => {
                                            const blockedUser = usersData[blockedUserId];
                                            return (
                                                <div key={blockedUserId} className="flex items-center justify-between gap-3 p-1.5 hover:bg-white/5 rounded-xl transition-all">
                                                    <div className="flex items-center gap-2.5 min-w-0">
                                                        <img 
                                                            src={blockedUser?.pfp || ''} 
                                                            alt={blockedUserId} 
                                                            className="w-8 h-8 rounded-full object-cover bg-white/10 flex-shrink-0" 
                                                            onError={(e) => {
                                                                (e.target as HTMLImageElement).src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23888888"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>';
                                                            }}
                                                        />
                                                        <div className="flex flex-col min-w-0">
                                                            <span className="text-xs font-bold leading-tight truncate" style={stickerTextStyle(0.18)}>{blockedUserId}</span>
                                                            <span className="text-[9px] opacity-50 leading-none truncate">@{blockedUserId.toLowerCase()}</span>
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => handleUnblockClick(blockedUserId)}
                                                        className="px-3 py-1 bg-white/10 hover:bg-red-500/20 hover:text-red-400 text-[9px] font-black uppercase tracking-wider rounded-lg transition-all flex-shrink-0"
                                                    >
                                                        Unblock
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })()}
                        </div>

                        <div className="space-y-3 pt-2 border-t border-white/5">
                            <button
                                onClick={handleDeactivate}
                                disabled={isDeactivating || isDeleting}
                                className="w-full bg-orange-600/20 hover:bg-orange-600/40 text-orange-400 py-3 rounded-xl transition-all font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                                {isDeactivating ? 'Deactivating...' : 'Deactivate Account'}
                            </button>
                            <p className="text-[10px] px-1 italic opacity-60" style={stickerTextStyle(0.18)}>Deactivating hides your profile until you contact support to reactivate it.</p>

                            <div className="pt-2">
                                <button
                                    onClick={handleDelete}
                                    disabled={isDeactivating || isDeleting}
                                    className="w-full bg-red-600/20 hover:bg-red-600/40 text-red-400 py-3 rounded-xl transition-all font-bold flex items-center justify-center gap-2 disabled:opacity-50 border border-red-900/30"
                                >
                                    {isDeleting ? 'Deleting...' : 'Delete Permanently'}
                                </button>
                                <p className="text-[10px] text-red-500/60 px-1 font-bold mt-1">CRITICAL: This deletes your account, all posts, and all data forever.</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

